export type SupportedLanguage = 'en' | 'es' | 'he' | 'ja';

export const supportedLanguages: Record<
  SupportedLanguage,
  { locale: string; direction: 'ltr' | 'rtl'; nativeName: string; englishName: string }
> = {
  en: { locale: 'en-US', direction: 'ltr', nativeName: 'English', englishName: 'English' },
  es: { locale: 'es-ES', direction: 'ltr', nativeName: 'Español', englishName: 'Spanish' },
  he: { locale: 'he-IL', direction: 'rtl', nativeName: 'עברית', englishName: 'Hebrew' },
  ja: { locale: 'ja-JP', direction: 'ltr', nativeName: '日本語', englishName: 'Japanese' },
};

export const normalizeLanguage = (language?: string | null): SupportedLanguage => {
  if (language === 'es' || language === 'he' || language === 'ja') {
    return language;
  }
  return 'en';
};

type TranslationMap = Record<string, string>;

export const formatFallbackLabel = (value?: string | null): string => {
  if (!value) {
    return 'Not set';
  }
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const formatTranslatedLabel = (
  t: (key: string) => string,
  value?: string | number | null,
): string => {
  if (value == null || value === '') {
    return t('notSet');
  }
  const key = String(value);
  const translationKey = key === 'open' ? 'openStatus' : key;
  const translated = t(translationKey);
  return translated === translationKey ? formatFallbackLabel(key) : translated;
};

export const translations: Record<SupportedLanguage, TranslationMap> = {
  en: {
    appSubtitle: 'Production Workspace',
    signInSubtitle: 'Sign in to your production workspace.',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    loadingWorkspace: 'Loading workspace...',
    primaryNavigation: 'Primary navigation',
    invalidEmailOrPassword: 'Invalid email or password.',
    projects: 'Projects',
    projectPortal: 'Project Portal',
    adminConsole: 'Admin Console',
    adminProjects: 'Admin Projects',
    adminProjectsDescription: 'Oversee project creation, ownership, access, and production status across the platform.',
    publishing: 'Publishing',
    users: 'Users',
    user: 'User',
    usersAndAccess: 'Users and Access',
    usersAndAccessDescription: 'Create platform users and keep access status visible before assigning them to projects.',
    refreshUsers: 'Refresh users',
    newUser: 'New User',
    createUser: 'Create User',
    createdUser: 'Created user',
    couldNotLoadUsers: 'Could not load users.',
    couldNotCreateUser: 'Could not create user.',
    couldNotUpdateUserStatus: 'Could not update user status.',
    username: 'Username',
    displayName: 'Display Name',
    phoneNumber: 'Phone Number',
    temporaryPassword: 'Temporary Password',
    temporaryPasswordHelper: 'Use at least 8 characters, or leave blank.',
    temporaryPasswordMinLength: 'Temporary password must be at least 8 characters.',
    emailRequired: 'Email is required.',
    enterEmailAddress: 'Enter an email address.',
    uiLanguage: 'UI Language',
    scope: 'Scope',
    admin: 'Admin',
    projectScoped: 'Project scoped',
    deactivateUser: 'Deactivate user',
    activateUser: 'Activate user',
    noUsersYet: 'No users yet.',
    creating: 'Creating...',
    audit: 'Audit',
    auditTrail: 'Audit Trail',
    auditTrailDescription: 'Review project creation, membership changes, status changes, and approval history.',
    couldNotLoadProjects: 'Could not load projects.',
    couldNotLoadAuditEvents: 'Could not load audit events.',
    openProject: 'Open Project',
    time: 'Time',
    action: 'Action',
    target: 'Target',
    actor: 'Actor',
    noAuditEventsForProject: 'No audit events for this project.',
    noProjectAuditEvents: 'No project audit events.',
    operations: 'Operations',
    projectWorkspace: 'Project Workspace',
    projectWorkspaceDescription: 'Manage the production pipeline around projects, people, languages, approvals, and audit history.',
    refreshProjects: 'Refresh projects',
    newProject: 'New Project',
    createUserFirst: 'Create a user before starting the first project.',
    activeProjects: 'Active Projects',
    inReview: 'In Review',
    pendingApproval: 'Pending Approval',
    project: 'Project',
    status: 'Status',
    storyType: 'Story Type',
    language: 'Language',
    owner: 'Owner',
    role: 'Role',
    actions: 'Actions',
    assign: 'Assign',
    assignTeam: 'Assign Team',
    unassign: 'Unassign',
    noProductionNotes: 'No production notes yet',
    unassigned: 'Unassigned',
    open: 'Open',
    noProjectsYet: 'No projects yet.',
    createProject: 'Create Project',
    projectName: 'Project Name',
    goalsAndInstructions: 'Goals and Instructions',
    projectManager: 'Project Manager',
    package: 'Package',
    primaryLanguage: 'Primary Language',
    cancel: 'Cancel',
    create: 'Create',
    saveProject: 'Save Project',
    publishingDashboard: 'Publishing Dashboard',
    publishingDashboardDescription: 'Track KDP packages and publishing submissions across active book projects.',
    refreshPublishingRequests: 'Refresh publishing requests',
    totalRequests: 'Total Requests',
    ready: 'Ready',
    submitted: 'Submitted',
    published: 'Published',
    allStatuses: 'All statuses',
    provider: 'Provider',
    allProviders: 'All providers',
    book: 'Book',
    submittedDate: 'Submitted',
    reference: 'Reference',
    none: 'None',
    notRecorded: 'Not recorded',
    noMarketplace: 'No marketplace',
    loadingPublishingRequests: 'Loading publishing requests...',
    noPublishingRequestsMatch: 'No publishing requests match these filters.',
    packageReady: 'Package',
    preflight: 'Preflight',
    settings: 'Settings',
    assets: 'Assets',
    interviewsAndTranscripts: 'Interview & Transcripts',
    interviewStudio: 'Interview Studio',
    conductAiInterview: 'Conduct AI interview',
    startAiInterview: 'Start AI interview',
    completeInterview: 'Complete interview',
    pause: 'Pause',
    resume: 'Resume',
    delete: 'Delete',
    aiInterviewer: 'AI interviewer',
    ai_agent: 'AI interviewer',
    intervieweeGender: 'Interviewee gender',
    interviewGoal: 'Interview goal',
    interviewOutline: 'Interview outline',
    interviewOutlinePlaceholder: 'One subject per line',
    confirmDeleteTranscript: 'Delete this transcript and all its versions?',
    couldNotDeleteTranscript: 'Could not delete the transcript.',
    couldNotConnectAiInterview: 'Could not connect to the AI interviewer.',
    couldNotAccessMicrophone: 'Could not access the microphone. Check browser permissions.',
    aiInterviewLiveHint: 'The live conversation transcript will appear here once the interview starts.',
    aiInterviewStatus_idle: 'Not started',
    aiInterviewStatus_connecting: 'Connecting…',
    aiInterviewStatus_in_progress: 'Live',
    aiInterviewStatus_paused: 'Paused',
    aiInterviewStatus_completed: 'Completed',
    aiInterviewStatus_error: 'Error',
    female: 'Female',
    male: 'Male',
    unspecified: 'Unspecified',
    storyStudio: 'Story Studio',
    storyOutlines: 'Story Outlines',
    storyFiles: 'Story Files',
    chapters: 'Chapters',
    targetLanguage: 'Target Language',
    textDirection: 'Text Direction',
    translateDraft: 'Translate Draft',
    translation: 'Translation',
    overview: 'Overview',
    team: 'Team',
    loadingProject: 'Loading project...',
    refreshProject: 'Refresh project',
    projectSnapshot: 'Project Snapshot',
    interviewSessions: 'Interview Sessions',
    transcripts: 'Transcripts',
    interviewTitle: 'Interview Title',
    type: 'Type',
    audio: 'Audio',
    video: 'Video',
    story_document: 'Story Document',
    document: 'Document',
    other: 'Other',
    interviewer: 'Interviewer',
    interviewee: 'Interviewee',
    scheduled: 'Scheduled',
    notScheduled: 'Not scheduled',
    minutes: 'Minutes',
    notes: 'Notes',
    add: 'Add',
    update: 'Update',
    generate: 'Generate',
    session: 'Session',
    participants: 'Participants',
    duration: 'Duration',
    pending: 'Pending',
    saved: 'saved',
    noInterviewSessionsYet: 'No interview sessions yet.',
    sourceArtifact: 'Source Artifact',
    confidence: 'Confidence',
    transcriptText: 'Transcript Text',
    source: 'Source',
    created: 'Created',
    manualTranscript: 'Manual transcript',
    editTranscript: 'Edit transcript',
    approveTranscript: 'Approve transcript',
    noTranscriptsYet: 'No transcripts yet.',
    artifacts: 'Artifacts',
    design: 'Design',
    interviews: 'Interviews',
    publishingStage: 'Publishing',
    reviews: 'Reviews',
    story: 'Story',
    workflow: 'Workflow',
    notSet: 'Not set',
    bookContent: 'Book Content',
    content: 'Content',
    image: 'Image',
    spacer: 'Spacer',
    pageBreak: 'Page break',
    subHeading: 'Sub-heading',
    subChapter: 'Sub-chapter',
    subSubChapter: 'Sub-sub-chapter',
    quotedText: 'Quoted text',
    bodyText: 'Body text',
    alignment: 'Alignment',
    moveUp: 'Move up',
    moveDown: 'Move down',
    addSection: 'Add section',
    sectionTitle: 'Section title',
    titleLevel: 'Title level',
    startsOn: 'Starts on',
    indentLevel: 'Indent level',
    headingText: 'Heading text',
    headingLevel: 'Heading level',
    paragraphText: 'Paragraph text',
    paragraphStyle: 'Style',
    addParagraph: 'Add paragraph',
    addSubChapter: 'Add sub-chapter',
    addSubSubChapter: 'Add sub-sub-chapter',
    deleteHeading: 'Delete title',
    deleteParagraph: 'Delete paragraph',
    mergeParagraph: 'Merge with next paragraph',
    imageLayout: 'Layout',
    layoutInline: 'Inline',
    layoutFloat: 'Float',
    layoutFullWidth: 'Full width',
    layoutFullPage: 'Full page',
    layoutFullBleed: 'Full bleed',
    sideNext: 'Next page',
    sideLeft: 'Left page',
    sideRight: 'Right page',
    untitledSection: 'Untitled section',
    fixedPages: 'fixed pages',
    fixedPage: 'Fixed page',
    fixedSpread: 'Fixed spread',
    noSectionsYet: 'No sections yet. Add one to start.',
    sectionEmpty: 'This section is empty. Add a block below.',
    dragImagesHint: 'Drag an image into the text to place it between paragraphs.',
    dragImageToPlace: 'Drag into the text to place',
    dropImageHere: 'Drop image here',
    removeSection: 'Remove section',
    spacerHeightCm: 'Height',
    breakToSide: 'Break to',
    fixedUnitPreserved: 'Fixed layout — edit in the canvas editor (coming soon). Preserved on save.',
    blocks: 'blocks',
    collapse: 'Collapse',
    expand: 'Expand',
    showMore: 'Show more',
    addPage: 'Add page',
    addSpread: 'Add spread',
    pageRole: 'Page role',
    roleContent: 'Content',
    roleFullBleedImage: 'Full-bleed image',
    roleTitle: 'Title',
    rolePartDivider: 'Part divider',
    textBlock: 'Text block',
    imageBlock: 'Image block',
    noPhoto: 'No photo',
    background: 'Background',
    backgroundType: 'Background type',
    backgroundImage: 'Background image',
    fit: 'Fit',
    fitCover: 'Cover',
    fitContain: 'Contain',
    focalX: 'Focal X %',
    focalY: 'Focal Y %',
    selectedBlock: 'Selected block',
    canvasEmpty: 'Empty page. Add an image or text block.',
    selectBlockHint: 'Select a block on the canvas to edit it.',
    removeBlock: 'Remove block',
    fontSize: 'Font size (pt)',
    positionX: 'Position X %',
    positionY: 'Position Y %',
    rotation: 'Rotation °',
    layer: 'Layer (z)',
    imageFilter: 'Filter',
    filterNone: 'None',
    filterGrayscale: 'Grayscale',
    filterSepia: 'Sepia',
    onPage: 'On page',
    bothPages: 'Both pages',
    frontMatter: 'Front matter',
    bodyContent: 'Body content',
    backMatter: 'Back matter',
    pageBackground: 'Page background',
    pageBackgroundHelp:
      'Set a default image painted behind every page. Use one image for all pages, or different images for left and right pages.',
    enableDefaultBackground: 'Enable default background',
    backgroundScope: 'Applies to',
    allPages: 'All pages',
    differentSides: 'Different left/right',
    placementCover: 'Cover (fill, crop)',
    placementContain: 'Contain (fit)',
    placementStretch: 'Stretch',
    placementTile: 'Tile',
    designWorkspace: 'Design Workspace',
    designParameters: 'Design Parameters',
    designTemplates: 'Design Templates',
    designTemplatesDescription: 'Reusable page size, font, header, and spacing presets that project users can apply to a book.',
    newDesignTemplate: 'New Template',
    editDesignTemplate: 'Edit Template',
    createDesignTemplate: 'Create Template',
    couldNotLoadDesignTemplates: 'Could not load design templates.',
    couldNotSaveDesignTemplate: 'Could not save design template.',
    designTemplateCreated: 'Design template created.',
    designTemplateUpdated: 'Design template updated.',
    noDesignTemplatesYet: 'No design templates yet.',
    templateNameRequired: 'Template name is required.',
    bookType: 'Book Type',
    basicInfo: 'Basic Info',
    indentationAndRules: 'Indentation & Rules',
    saveChanges: 'Save Changes',
    archive: 'Archive',
    restore: 'Restore',
    archived: 'Archived',
    edit: 'Edit',
    duplicate: 'Duplicate',
    copyNoun: 'copy',
    headers: 'Headers',
    picture: 'Picture',
    mixed: 'Mixed',
    applyTemplate: 'Apply Template',
    applyDesignTemplate: 'Apply Design Template',
    selectTemplate: 'Select Template',
    noDesignTemplatesAvailable: 'No design templates are available yet.',
    applyingTemplateReplacesSettings: 'Applying a template replaces the current print, font, and spacing settings below with the ones from the template. It does not save automatically -- review and apply from the parameter dialogs, or use Save Book, to keep it.',
    templateApplied: 'Template applied. Review the settings and save to keep them.',
    bookDesignDrafts: 'Book Designs',
    activeBookDesignDraft: 'Active Design',
    noActiveBookDesignDraft: 'No active design',
    selectOrCreateBookDesignDraft: 'Select an existing book design or create a new one before editing the design.',
    createBookDesignDraft: 'Create Design',
    bookDesignDraftName: 'Design Name',
    bookDesignDraftNameRequired: 'Design name is required.',
    couldNotCreateBookDesignDraft: 'Could not create book design.',
    couldNotDeleteBookDesignDraft: 'Could not delete book design.',
    designTemplate: 'Design Template',
    noTemplate: 'No template',
    creatingUserRecordedFromSession: 'Creating user will be recorded from the current session.',
    selectBookDesignDraft: 'Select Design',
    noBookDesignDraftsYet: 'No book designs yet.',
    deleteBookDesignDraft: 'Delete book design',
    bookDetails: 'Book Details',
    coverDesign: 'Cover Design',
    startDesignDraft: 'Start Design',
    noDesignArtifactsYet: 'No design artifacts yet.',
    parameter: 'Parameter',
    value: 'Value',
    unit: 'Unit',
    cm: 'cm',
    element: 'Element',
    lineHeight: 'Line Height',
    style: 'Style',
    paragraph: 'Paragraph',
    photos: 'Photos',
    cover: 'Cover',
    printParameters: 'Print Parameters',
    fonts: 'Fonts',
    indentations: 'Indentations',
    rules: 'Rules',
    typography: 'Typography',
    visuals: 'Visuals',
    rendererPreview: 'Renderer Preview',
    noExactPreviewYet: 'No Exact Preview Yet',
    exactPreviewRequiresRenderer: 'Refresh the renderer preview to see the same artifact that will be used for PDF output.',
    loadingPreview: 'Loading preview...',
    couldNotLoadPdfPreview: 'Could not load PDF preview.',
    previewMode: 'Preview mode',
    singlePage: 'Single page',
    twoPageSpread: 'Two-page spread',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    resetZoomToFit: 'Fit to frame',
    actualSize: 'Actual size (click to zoom to 100%)',
    production: 'Production',
    runPreflight: 'Run Preflight',
    sendToPrinting: 'Send To Printing',
    publishingNotes: 'Publishing Notes',
    printProvider: 'Print Provider',
    submissionTracker: 'Submission Tracker',
    history: 'History',
    title: 'Title',
    subtitle: 'Subtitle',
    authorName: 'Author Name',
    direction: 'Direction',
    manuscriptText: 'Manuscript Text',
    searchManuscript: 'Search Manuscript',
    draft: 'Draft',
    intake: 'Intake',
    interview_planned: 'Interview Planned',
    interview_in_progress: 'Interview In Progress',
    transcription_in_progress: 'Transcription In Progress',
    story_outline_in_progress: 'Story Outline In Progress',
    story_writing_in_progress: 'Story Writing In Progress',
    review_in_progress: 'Review In Progress',
    design_in_progress: 'Design In Progress',
    approval_pending: 'Approval Pending',
    publishing_in_progress: 'Publishing In Progress',
    completed: 'Completed',
    on_hold: 'On Hold',
    cancelled: 'Cancelled',
    package_ready: 'Package Ready',
    changes_requested: 'Changes Requested',
    approved: 'Approved',
    rejected: 'Rejected',
    active: 'Active',
    resolved: 'Resolved',
    openStatus: 'Open',
    deactivated: 'Deactivated',
    publishing_ready: 'Publishing Ready',
    design_ready: 'Design Ready',
    passed: 'Passed',
    failed: 'Failed',
    warning: 'Warning',
    life_book: 'Life or Legacy Book',
    family_book: 'Family Book',
    community_book: 'Community Book',
    cookbook: 'Cookbook',
    picture_book: 'Picture Book',
    video_clip: 'Video Clip',
    presentation: 'Presentation',
    self_service: 'Self Service',
    guided: 'Guided',
    full_service: 'Full Service',
    project_manager: 'Project Manager',
    supervisor: 'Supervisor',
    service_provider: 'Service Provider',
    writer: 'Writer',
    designer: 'Designer',
    reviewer: 'Reviewer',
    amazon_kdp: 'Amazon KDP',
    other_provider: 'Other Provider',
    print_pdf: 'Print PDF',
    proof_pdf: 'Proof PDF',
    renderer_preview: 'Renderer Preview',
    draft_design: 'Draft Design',
    approved_for_export: 'Approved Design',
    layout_review: 'Layout Review',
    story_chapter: 'Story Chapter',
    regular_text: 'Regular Text',
    chapter_title: 'Chapter Title',
    sub_chapter: 'Sub Chapter',
    sub_sub_chapter: 'Sub Sub Chapter',
    quoted_text: 'Quoted Text',
    after_paragraph: 'After Paragraph',
    top_of_next_page: 'Top Of Page',
    full_page: 'Full Page',
    absolute: 'Positioned On Page',
    next: 'Next',
    left: 'Left',
    right: 'Right',
    center: 'Center',
    outer: 'Outer',
    inner: 'Inner',
    justify: 'Justify',
    front: 'Front',
    back: 'Back',
    photo: 'Photo',
    text: 'Text',
    grayscale: 'Grayscale',
    sepia: 'Sepia',
    page: 'Page',
    check: 'Check',
    createBook: 'Create Book',
    matches: 'Matches',
    placed: 'placed',
    items: 'items',
    anchor: 'Anchor',
    placement: 'Placement',
    pageSide: 'Page Side',
    align: 'Align',
    rotate: 'Rotate',
    filter: 'Filter',
    cropTopPercent: 'Crop Top %',
    cropRightPercent: 'Crop Right %',
    cropBottomPercent: 'Crop Bottom %',
    cropLeftPercent: 'Crop Left %',
    caption: 'Caption',
    addPhoto: 'Add Photo',
    layout: 'Layout',
    movePhotoToCurrentPosition: 'Move photo to current position',
    removePhoto: 'Remove photo',
    noPhotosPlacedYet: 'No photos placed yet.',
    side: 'Side',
    item: 'Item',
    position: 'Position',
    coverText: 'Cover Text',
    coverBackgroundFront: 'Front background color',
    coverBackgroundBack: 'Back background color',
    coverDefaultFont: 'Theme default',
    addCover: 'Add Cover',
    moveCoverItemToCurrentPosition: 'Move cover item to current position',
    removeCoverItem: 'Remove cover item',
    noCoverItemsYet: 'No cover items yet.',
    widthCm: 'Width',
    heightCm: 'Height',
    textAlign: 'Text Align',
    startPage: 'Start Page',
    innerMarginCm: 'Inner Margin',
    outerMarginCm: 'Outer Margin',
    topMarginCm: 'Top Margin',
    bottomMarginCm: 'Bottom Margin',
    rightPageHeader: 'Right Page Header',
    leftPageHeader: 'Left Page Header',
    headerTokensHint: 'Tokens: {page_number}, {book_title}, {author_name}, {chapter_title}',
    rightHeaderTopCm: 'Right Header Top',
    leftHeaderTopCm: 'Left Header Top',
    rightHeaderAlign: 'Right Header Align',
    leftHeaderAlign: 'Left Header Align',
    font: 'Font',
    size: 'Size',
    color: 'Color',
    bold: 'Bold',
    italic: 'Italic',
    leftToRight: 'Left to Right',
    rightToLeft: 'Right to Left',
    spacingAndRules: 'Spacing And Rules',
    paragraphIndentCm: 'Paragraph Indent',
    subParagraphCm: 'Sub Paragraph',
    subSubParagraphCm: 'Sub Sub Paragraph',
    chapterStart: 'Chapter Start',
    contentIndex: 'Contents',
    generateContentIndex: 'Generate Contents',
    contentIndexTitle: 'Contents Title',
    includeSubChapters: 'Include Subchapters',
    includeSubSubChapters: 'Include Sub-subchapters',
    contentIndexDotLeaders: 'Dot Leaders',
    leftPage: 'Left Page',
    rightPage: 'Right Page',
    previousPage: 'Previous Page',
    nextPage: 'Next Page',
    chapterAboveCm: 'Chapter Above',
    chapterBelowCm: 'Chapter Below',
    subChapterAboveCm: 'Sub Chapter Above',
    subChapterBelowCm: 'Sub Chapter Below',
    subSubAboveCm: 'Sub Sub Above',
    subSubBelowCm: 'Sub Sub Below',
    textAboveCm: 'Text Above',
    textBelowCm: 'Text Below',
    paragraphEndMarker: 'Paragraph End Marker',
    marker: 'Marker',
    markerOffsetCm: 'Marker Offset',
    oneWord: 'One Word',
    oneLine: 'One Line',
    pages: 'pages',
    refreshPreview: 'Refresh Preview',
    renderedBookProof: 'Rendered book proof',
    untitledBook: 'Untitled Book',
    noManuscriptTextYet: 'No manuscript text yet.',
    saveBook: 'Save Book',
    applyAndRefreshPreview: 'Apply & Refresh Preview',
    versionNote: 'Version Note',
    saveVersion: 'Save Version',
    approveBook: 'Approve Book',
    exportType: 'Export Type',
    requestExport: 'Request Export',
    export: 'Export',
    failedCount: 'failed',
    warningsCount: 'warnings',
    message: 'Message',
    noMessageRecorded: 'No message recorded.',
    noPreflightReport: 'No preflight report has been recorded for this export.',
    noExportReadyForPreflight: 'No export is ready for preflight.',
    marketplace: 'Marketplace',
    reviewerNotes: 'Reviewer Notes',
    interior: 'Interior',
    save: 'Save',
    accept: 'Accept',
    acceptAsNewChapter: 'Accept as New Chapter',
    acceptedGeneratedChapter: 'Accepted generated chapter',
    ai: 'AI',
    aiChapterFromSources: 'AI Chapter From Sources',
    ai_draft: 'AI Draft',
    appendToEmptyDraft: 'Append to empty draft',
    approve: 'Approve',
    approveStoryChapter: 'Approve story chapter',
    approveStoryOutline: 'Approve story outline',
    approvedOutline: 'Approved Outline',
    approvedStoryOutlineRequired: 'Approved story outline is required.',
    approvedTranscript: 'Approved Transcript',
    approvedTranscriptRequired: 'Approved transcript is required.',
    artifact: 'Artifact',
    howAdded: 'How Added',
    creatingUser: 'Creating User',
    current: 'Current',
    nameDescription: 'Name / Description',
    commonParameters: 'Common Parameters',
    fileDetails: 'File Details',
    openArtifactFile: 'Open artifact file',
    openExternalSource: 'Open external source',
    noPreviewAvailable: 'No preview available.',
    uploaded: 'Uploaded',
    linked: 'Linked',
    close: 'Close',
    artifactTitleRequired: 'Artifact title is required.',
    asin: 'ASIN',
    askForEditOrInsertion: 'Ask for an edit or insertion',
    assistant: 'Assistant',
    author: 'Author',
    bookTitleRequired: 'Book title is required.',
    changeNote: 'Change Note',
    changes: 'Changes',
    chapter: 'Chapter',
    book_chapter: 'Chapter',
    book_outline: 'Outline',
    preface: 'Preface',
    acknowledgement: 'Acknowledgements',
    introduction: 'Introduction',
    afterword: 'Afterword',
    custom: 'Custom',
    chapterBody: 'Chapter Body',
    chapterTitle: 'Chapter Title',
    chat: 'Chat',
    chooseRecording: 'Choose recording',
    chooseRecordingBeforeGeneratingTranscript: 'Choose a recording before generating a transcript.',
    chooseTranscriptFile: 'Choose transcript file',
    chooseSessionAndRecordingFileFirst: 'Choose a session and recording file first.',
    comment: 'Comment',
    comments: 'Comments',
    couldNotAcceptGeneratedChapter: 'Could not accept generated chapter.',
    couldNotAddReviewComment: 'Could not add review comment.',
    couldNotApproveBookResolveComments: 'Could not approve the book. Resolve open comments first.',
    couldNotApproveStoryChapter: 'Could not approve story chapter.',
    couldNotApproveStoryOutline: 'Could not approve story outline.',
    couldNotApproveTranscript: 'Could not approve transcript.',
    couldNotAssignProjectMember: 'Could not assign project member.',
    couldNotCheckServiceStatus: 'Could not check service status.',
    couldNotCreateBook: 'Could not create book.',
    couldNotCreateInterviewSession: 'Could not create interview session.',
    couldNotCreateProject: 'Could not create the project.',
    couldNotCreateStoryArtifact: 'Could not create story artifact.',
    couldNotGenerateChapterFromSelectedSources: 'Could not generate a chapter from the selected sources.',
    couldNotGenerateStoryChapters: 'Could not generate story chapters.',
    couldNotGenerateStoryOutline: 'Could not generate story outline.',
    couldNotGenerateTranscript: 'Could not generate transcript.',
    couldNotLoadBookWorkflow: 'Could not load book workflow.',
    couldNotLoadInterviewStudio: 'Could not load Interview Studio.',
    couldNotLoadProjectWorkspace: 'Could not load project workspace.',
    couldNotLoadProjectWorkspaceData: 'Could not load project workspace data.',
    couldNotLoadPublishingRequests: 'Could not load publishing requests.',
    couldNotLoadWritingStudio: 'Could not load Writing Studio.',
    couldNotPrepareTranslationSuggestions: 'Could not prepare translation suggestions.',
    couldNotReadTranscriptFile: 'Could not read transcript file.',
    couldNotRecordReviewDecision: 'Could not record review decision.',
    couldNotRefineTranscript: 'Could not refine transcript.',
    couldNotRefreshRendererPreview: 'Could not refresh renderer preview.',
    couldNotRequestExportApproveBook: 'Could not request export. Approve the book first.',
    couldNotRequestReview: 'Could not request review.',
    couldNotRunExportPreflight: 'Could not run export preflight.',
    couldNotSaveArtifact: 'Could not save artifact.',
    couldNotSaveBookNeedsDraft: 'Could not save book. A manuscript draft is required.',
    bookLockedForEditing: 'This book design cannot be edited in its current status. Your changes were not saved and the preview was not refreshed.',
    couldNotSaveBookVersion: 'Could not save book version.',
    couldNotSaveProject: 'Could not save project.',
    couldNotSaveStoryArtifactVersion: 'Could not save story artifact version.',
    couldNotSaveStoryChapter: 'Could not save story chapter.',
    couldNotSaveStoryOutline: 'Could not save story outline.',
    couldNotSaveTranscript: 'Could not save transcript.',
    couldNotSaveZoomMeetingLink: 'Could not save Zoom meeting link.',
    couldNotSendExportToPrinting: 'Could not send export to printing.',
    couldNotSendStoryDocumentToDesign: 'Could not send story document to design.',
    couldNotSendStoryDocumentToPublishing: 'Could not send story document to publishing.',
    couldNotStartReview: 'Could not start review.',
    couldNotUnassignProjectMember: 'Could not unassign project member.',
    couldNotUpdatePublishingSubmission: 'Could not update publishing submission.',
    couldNotUpdateStoryDocument: 'Could not update story document.',
    couldNotUploadRecording: 'Could not upload recording.',
    couldNotUploadStoryDocument: 'Could not upload story document.',
    createFirstStoryArtifactFromEditor: 'Create the first story artifact from the editor.',
    createStoryArtifact: 'Create story artifact',
    createdFromWritingStudio: 'Created from Writing Studio',
    currentDraft: 'Current draft',
    currentStatus: 'Current Status',
    currentTarget: 'Current target',
    decision: 'Decision',
    decisionNote: 'Decision Note',
    description: 'Description',
    designReady: 'Design Ready',
    diffAcceptReject: 'Diff / Accept / Reject',
    dismiss: 'Dismiss',
    dismissed: 'Dismissed',
    editStoryArtifact: 'Edit story artifact',
    editStoryChapter: 'Edit story chapter',
    editStoryOutline: 'Edit story outline',
    editableStoryText: 'Editable Story Text',
    empty: 'Empty',
    end: 'End',
    enterStoryTextBeforeTranslating: 'Enter story text before translating.',
    exportFailedPreflightReviewReport: 'Export failed the preflight review. Check the report.',
    externalUrl: 'External URL',
    file: 'File',
    format: 'Format',
    aiTranscriptRefinement: 'AI transcript refinement',
    generateChapter: 'Generate Chapter',
    generateTranscript: 'Generate transcript',
    generatedByWriterAgent: 'Generated by writer agent',
    generatedChapterAcceptedAsNewStoryArtifact: 'Generated chapter accepted as a new story artifact.',
    generatedDraft: 'Generated draft',
    generationInstructions: 'Generation instructions',
    heightPercent: 'Height %',
    interviewStudioDescription: 'Session to recording to transcript to approved source material.',
    interviewStudioRecordingFor: 'Interview Studio recording for {title}',
    interviewSession: 'Interview Session',
    interviewTitleAndIntervieweeRequired: 'Interview title and interviewee are required.',
    invalidProjectId: 'Invalid project id.',
    isbn: 'ISBN',
    join: 'Join',
    loadProjectToUseInterviewStudio: 'Load a project to use Interview Studio.',
    ltr: 'LTR',
    manualInterviewStudioEdit: 'Manual Interview Studio edit',
    manualStudioSave: 'Manual Studio save',
    meetingLink: 'Meeting Link',
    method: 'Method',
    min: 'min',
    modifiable: 'Modifiable',
    newChapter: 'New chapter',
    newManualTranscript: 'New manual transcript',
    no: 'No',
    noArtifactsYet: 'No artifacts yet.',
    noDescription: 'No description',
    noFileSelected: 'No file selected',
    noPendingSuggestions: 'No pending suggestions.',
    noPreview: 'No preview',
    noReviewCommentsYet: 'No review comments yet.',
    noReviewsRequestedYet: 'No reviews requested yet.',
    noSessionsYet: 'No sessions yet.',
    noStoryArtifactSelected: 'No story artifact selected',
    noStoryDocumentsReadyYet: 'No story documents ready yet.',
    noStoryOutlinesYet: 'No story outlines yet.',
    noStudioVersionsYet: 'No Studio versions yet.',
    noSummary: 'No summary',
    noTextSourcesAvailableYet: 'No text sources are available yet.',
    openReviews: 'Open Reviews',
    operationsDescription: 'Monitor production service health.',
    order: 'Order',
    outline: 'Outline',
    outlineJson: 'Outline JSON',
    outlineTitle: 'Outline Title',
    projectTitleAndLanguageRequired: 'Project title and language are required.',
    projectTitleOwnerAndLanguageRequired: 'Project title, owner, and language are required.',
    proposedText: 'Proposed text',
    publish: 'Publish',
    publishingReady: 'Publishing Ready',
    rangeOnly: 'Range only',
    recording: 'Recording',
    recordingDescription: 'Upload audio/video after a Zoom session or from an external recorder.',
    refineTranscript: 'Refine transcript by AI',
    refresh: 'Refresh',
    refreshServiceHealth: 'Refresh service health',
    refreshingWritingStudio: 'Refreshing Writing Studio...',
    reject: 'Reject',
    reopen: 'Reopen',
    request: 'Request',
    resolve: 'Resolve',
    restoreToEditor: 'Restore to editor',
    review: 'Review',
    reviewCommentStatusFilter: 'Review comment status filter',
    reviewCommentsNeedAnchor: 'Review comments need an anchor.',
    reviewCommentsYet: 'review comments yet.',
    reviewInstructions: 'Review Instructions',
    reviewTarget: 'Review Target',
    reviewTargetAndCommentRequired: 'Review target and comment are required.',
    reviewTargetRequired: 'Review target is required.',
    reviewTargetType: 'Review Target Type',
    reviewTitle: 'Review Title',
    reviewable: 'Reviewable',
    rtl: 'RTL',
    saveText: 'Save Text',
    saveZoom: 'Save Zoom',
    savedVersion: 'Saved version {version}.',
    sections: 'Sections',
    select: 'Select',
    selectAtLeastOneSourceBeforeGenerating: 'Select at least one source before generating.',
    selectBookAndEnterTitle: 'Select a book and enter a title.',
    selectEditableStudioArtifactAndEnterTitle: 'Select an editable Studio artifact and enter a title.',
    selectProjectToOpenWritingStudio: 'Select a project to open Writing Studio.',
    selectSession: 'Select a session',
    selectStoryDocument: 'Select story document',
    selectedSources: 'Selected sources',
    selectedText: 'Selected Text',
    selectedTextOrTargetNote: 'Selected text or target note',
    send: 'Send',
    sessions: 'Sessions',
    scheduleInterview: 'Schedule interview',
    sources: 'Sources',
    start: 'Start',
    startTranslationDraft: 'Start translation draft',
    storyArtifactCreated: 'Story artifact created.',
    storyArtifacts: 'Story Artifacts',
    storyChapter: 'Story Chapter',
    storyChapterTitleAndBodyRequired: 'Story chapter title and body are required.',
    storyDocument: 'Story Document',
    storyDocuments: 'Story Documents',
    storyDocumentsRequireWordOrPdf: 'Story documents require a Word or PDF file.',
    storyDocumentUploaded: 'Story document uploaded.',
    storyOutline: 'Story Outline',
    storyOutlineJsonMustBeValid: 'Story outline JSON must be valid.',
    storyOutlineTitleRequired: 'Story outline title is required.',
    storyPipelineItemCreateStudioArtifact: 'This item comes from the existing story pipeline. Create a Studio artifact to edit and version manually here.',
    storyText: 'Story text',
    suggestionAppliedSaveToCreateVersion: 'Suggestion applied to the editor draft. Save to create a version.',
    suggestions: 'Suggestions',
    summary: 'Summary',
    targetDirection: 'Target Dir',
    targetType: 'Target Type',
    textArtifact: 'Text Artifact',
    textContent: 'Text Content',
    titleRequired: 'Title is required.',
    total: 'Total',
    transcript: 'Transcript',
    transcriptEditorDescription: 'Edit manually while playing the recording. Every save creates a transcript version.',
    transcriptSessionAndSourceArtifactRequired: 'Transcript session and source artifact are required.',
    transcriptSessionAndTextRequired: 'Transcript session and text are required.',
    transcriptTextRequired: 'Transcript text is required.',
    translationDraft: 'Translation draft',
    translationDraftFromTranscript: 'Translation draft from transcript #{id}',
    translationSuggestionsReady: 'Translation suggestions are ready for review.',
    unsavedManuscriptEditsVisible: 'Unsaved manuscript edits are visible in the preview.',
    updated: 'Updated',
    upload: 'Upload',
    uploadArtifact: 'Upload artifact',
    uploadInterviewRecording: 'Upload interview recording',
    uploadRecording: 'Upload recording',
    uploadStoryDocument: 'Upload story document',
    uploadTranscript: 'Upload transcript',
    uploader: 'Uploader',
    versionHistory: 'Version History',
    versions: 'versions',
    widthPercent: 'Width %',
    words: 'Words',
    writerChat: 'Writer Chat',
    writerChatCouldNotRespond: 'Writer chat could not respond.',
    xPercent: 'X %',
    yPercent: 'Y %',
    zoom: 'Zoom',
    zoomLink: 'Zoom link',
    zoomMeetingLink: 'Zoom meeting link',
    zoomMeetingLinkMustBeHttps: 'Zoom meeting link must be an HTTPS zoom.us URL.',
    zoomMeetingLinkRequired: 'Zoom meeting link is required.',
  },
  es: {
    appSubtitle: 'Espacio de producción',
    signInSubtitle: 'Inicia sesión en tu espacio de producción.',
    email: 'Correo electrónico',
    password: 'Contraseña',
    signIn: 'Iniciar sesión',
    signOut: 'Cerrar sesión',
    loadingWorkspace: 'Cargando espacio de trabajo...',
    primaryNavigation: 'Navegación principal',
    invalidEmailOrPassword: 'Correo o contraseña inválidos.',
    projects: 'Proyectos',
    projectPortal: 'Portal de proyectos',
    adminConsole: 'Consola de administración',
    adminProjects: 'Proyectos de administración',
    adminProjectsDescription: 'Supervisa creación, responsables, acceso y estado de producción de los proyectos en la plataforma.',
    publishing: 'Publicación',
    users: 'Usuarios',
    user: 'Usuario',
    usersAndAccess: 'Usuarios y acceso',
    usersAndAccessDescription: 'Crea usuarios de la plataforma y mantén visible su estado antes de asignarlos a proyectos.',
    refreshUsers: 'Actualizar usuarios',
    newUser: 'Nuevo usuario',
    createUser: 'Crear usuario',
    createdUser: 'Usuario creado',
    couldNotLoadUsers: 'No se pudieron cargar los usuarios.',
    couldNotCreateUser: 'No se pudo crear el usuario.',
    couldNotUpdateUserStatus: 'No se pudo actualizar el estado del usuario.',
    username: 'Nombre de usuario',
    displayName: 'Nombre visible',
    phoneNumber: 'Teléfono',
    temporaryPassword: 'Contraseña temporal',
    temporaryPasswordHelper: 'Usa al menos 8 caracteres, o déjalo en blanco.',
    temporaryPasswordMinLength: 'La contraseña temporal debe tener al menos 8 caracteres.',
    emailRequired: 'El correo electrónico es obligatorio.',
    enterEmailAddress: 'Ingresa una dirección de correo electrónico.',
    uiLanguage: 'Idioma de la interfaz',
    scope: 'Alcance',
    admin: 'Administrador',
    projectScoped: 'Limitado a proyectos',
    deactivateUser: 'Desactivar usuario',
    activateUser: 'Activar usuario',
    noUsersYet: 'Aún no hay usuarios.',
    creating: 'Creando...',
    audit: 'Auditoría',
    auditTrail: 'Registro de auditoría',
    auditTrailDescription: 'Revisa la creación de proyectos, cambios de miembros, cambios de estado e historial de aprobaciones.',
    couldNotLoadProjects: 'No se pudieron cargar los proyectos.',
    couldNotLoadAuditEvents: 'No se pudieron cargar los eventos de auditoría.',
    openProject: 'Abrir proyecto',
    time: 'Hora',
    action: 'Acción',
    target: 'Destino',
    actor: 'Actor',
    noAuditEventsForProject: 'No hay eventos de auditoría para este proyecto.',
    noProjectAuditEvents: 'No hay eventos de auditoría del proyecto.',
    operations: 'Operaciones',
    projectWorkspace: 'Espacio de proyectos',
    projectWorkspaceDescription: 'Gestiona la producción de proyectos, personas, idiomas, aprobaciones y auditoría.',
    refreshProjects: 'Actualizar proyectos',
    newProject: 'Nuevo proyecto',
    createUserFirst: 'Crea un usuario antes de iniciar el primer proyecto.',
    activeProjects: 'Proyectos activos',
    inReview: 'En revisión',
    pendingApproval: 'Pendiente de aprobación',
    project: 'Proyecto',
    status: 'Estado',
    storyType: 'Tipo de historia',
    language: 'Idioma',
    owner: 'Responsable',
    role: 'Rol',
    actions: 'Acciones',
    assign: 'Asignar',
    assignTeam: 'Asignar equipo',
    unassign: 'Quitar',
    noProductionNotes: 'Aún no hay notas de producción',
    unassigned: 'Sin asignar',
    open: 'Abrir',
    noProjectsYet: 'Aún no hay proyectos.',
    createProject: 'Crear proyecto',
    projectName: 'Nombre del proyecto',
    goalsAndInstructions: 'Objetivos e instrucciones',
    projectManager: 'Gestor del proyecto',
    package: 'Paquete',
    primaryLanguage: 'Idioma principal',
    cancel: 'Cancelar',
    create: 'Crear',
    saveProject: 'Guardar proyecto',
    publishingDashboard: 'Panel de publicación',
    publishingDashboardDescription: 'Supervisa paquetes KDP y envíos de publicación en todos los proyectos activos.',
    refreshPublishingRequests: 'Actualizar solicitudes de publicación',
    totalRequests: 'Solicitudes totales',
    ready: 'Listo',
    submitted: 'Enviado',
    published: 'Publicado',
    allStatuses: 'Todos los estados',
    provider: 'Proveedor',
    allProviders: 'Todos los proveedores',
    book: 'Libro',
    submittedDate: 'Enviado',
    reference: 'Referencia',
    none: 'Ninguna',
    notRecorded: 'No registrado',
    noMarketplace: 'Sin marketplace',
    loadingPublishingRequests: 'Cargando solicitudes de publicación...',
    noPublishingRequestsMatch: 'No hay solicitudes de publicación con estos filtros.',
    packageReady: 'Paquete',
    preflight: 'Preflight',
    settings: 'Configuración',
    assets: 'Recursos',
    interviewsAndTranscripts: 'Entrevistas y transcripciones',
    interviewStudio: 'Estudio de entrevistas',
    storyStudio: 'Estudio de historia',
    storyOutlines: 'Esquemas de historia',
    storyFiles: 'Archivos de historia',
    chapters: 'Capítulos',
    targetLanguage: 'Idioma de destino',
    textDirection: 'Dirección del texto',
    translateDraft: 'Traducir borrador',
    translation: 'Traducción',
    overview: 'Resumen',
    team: 'Equipo',
    loadingProject: 'Cargando proyecto...',
    refreshProject: 'Actualizar proyecto',
    projectSnapshot: 'Resumen del proyecto',
    interviewSessions: 'Sesiones de entrevista',
    transcripts: 'Transcripciones',
    interviewTitle: 'Título de entrevista',
    type: 'Tipo',
    audio: 'Audio',
    video: 'Video',
    story_document: 'Documento de historia',
    document: 'Documento',
    other: 'Otro',
    interviewer: 'Entrevistador',
    interviewee: 'Entrevistado',
    scheduled: 'Programado',
    notScheduled: 'No programado',
    minutes: 'Minutos',
    notes: 'Notas',
    add: 'Agregar',
    update: 'Actualizar',
    generate: 'Generar',
    session: 'Sesión',
    participants: 'Participantes',
    duration: 'Duración',
    pending: 'Pendiente',
    saved: 'guardado',
    noInterviewSessionsYet: 'Aún no hay sesiones de entrevista.',
    sourceArtifact: 'Material fuente',
    confidence: 'Confianza',
    transcriptText: 'Texto de la transcripción',
    source: 'Fuente',
    created: 'Creado',
    manualTranscript: 'Transcripción manual',
    editTranscript: 'Editar transcripción',
    approveTranscript: 'Aprobar transcripción',
    noTranscriptsYet: 'Aún no hay transcripciones.',
    artifacts: 'Materiales',
    design: 'Diseño',
    interviews: 'Entrevistas',
    publishingStage: 'Publicación',
    reviews: 'Revisiones',
    story: 'Historia',
    workflow: 'Flujo',
    notSet: 'Sin definir',
    bookContent: 'Contenido del libro',
    content: 'Contenido',
    image: 'Imagen',
    spacer: 'Espaciador',
    pageBreak: 'Salto de página',
    subHeading: 'Subtítulo',
    subChapter: 'Subcapítulo',
    subSubChapter: 'Sub-subcapítulo',
    quotedText: 'Texto citado',
    bodyText: 'Texto normal',
    alignment: 'Alineación',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    addSection: 'Añadir sección',
    sectionTitle: 'Título de sección',
    titleLevel: 'Nivel de título',
    startsOn: 'Comienza en',
    indentLevel: 'Nivel de sangría',
    headingText: 'Texto del título',
    headingLevel: 'Nivel de título',
    paragraphText: 'Texto del párrafo',
    paragraphStyle: 'Estilo',
    addParagraph: 'Añadir párrafo',
    addSubChapter: 'Añadir subcapítulo',
    addSubSubChapter: 'Añadir sub-subcapítulo',
    deleteHeading: 'Eliminar título',
    deleteParagraph: 'Eliminar párrafo',
    mergeParagraph: 'Fusionar con el párrafo siguiente',
    imageLayout: 'Disposición',
    layoutInline: 'En línea',
    layoutFloat: 'Flotante',
    layoutFullWidth: 'Ancho completo',
    layoutFullPage: 'Página completa',
    layoutFullBleed: 'A sangre',
    sideNext: 'Página siguiente',
    sideLeft: 'Página izquierda',
    sideRight: 'Página derecha',
    untitledSection: 'Sección sin título',
    fixedPages: 'páginas fijas',
    fixedPage: 'Página fija',
    fixedSpread: 'Doble página fija',
    noSectionsYet: 'Aún no hay secciones. Añade una para empezar.',
    sectionEmpty: 'Esta sección está vacía. Añade un bloque abajo.',
    dragImagesHint: 'Arrastra una imagen al texto para colocarla entre párrafos.',
    dragImageToPlace: 'Arrastra al texto para colocar',
    dropImageHere: 'Suelta la imagen aquí',
    removeSection: 'Eliminar sección',
    spacerHeightCm: 'Altura',
    breakToSide: 'Saltar a',
    fixedUnitPreserved: 'Diseño fijo: edítalo en el editor de lienzo (próximamente). Se conserva al guardar.',
    blocks: 'bloques',
    collapse: 'Contraer',
    expand: 'Expandir',
    showMore: 'Mostrar más',
    addPage: 'Añadir página',
    addSpread: 'Añadir doble página',
    pageRole: 'Rol de página',
    roleContent: 'Contenido',
    roleFullBleedImage: 'Imagen a sangre',
    roleTitle: 'Título',
    rolePartDivider: 'Separador de parte',
    textBlock: 'Bloque de texto',
    imageBlock: 'Bloque de imagen',
    noPhoto: 'Sin foto',
    background: 'Fondo',
    backgroundType: 'Tipo de fondo',
    backgroundImage: 'Imagen de fondo',
    fit: 'Ajuste',
    fitCover: 'Cubrir',
    fitContain: 'Contener',
    focalX: 'Foco X %',
    focalY: 'Foco Y %',
    selectedBlock: 'Bloque seleccionado',
    canvasEmpty: 'Página vacía. Añade un bloque de imagen o texto.',
    selectBlockHint: 'Selecciona un bloque en el lienzo para editarlo.',
    removeBlock: 'Eliminar bloque',
    fontSize: 'Tamaño de fuente (pt)',
    positionX: 'Posición X %',
    positionY: 'Posición Y %',
    rotation: 'Rotación °',
    layer: 'Capa (z)',
    imageFilter: 'Filtro',
    filterNone: 'Ninguno',
    filterGrayscale: 'Escala de grises',
    filterSepia: 'Sepia',
    onPage: 'En página',
    bothPages: 'Ambas páginas',
    frontMatter: 'Páginas preliminares',
    bodyContent: 'Contenido principal',
    backMatter: 'Páginas finales',
    pageBackground: 'Fondo de página',
    pageBackgroundHelp:
      'Define una imagen predeterminada detrás de cada página. Usa una imagen para todas las páginas o imágenes distintas para izquierda y derecha.',
    enableDefaultBackground: 'Activar fondo predeterminado',
    backgroundScope: 'Se aplica a',
    allPages: 'Todas las páginas',
    differentSides: 'Izquierda/derecha distintas',
    placementCover: 'Cubrir (rellenar, recortar)',
    placementContain: 'Contener (ajustar)',
    placementStretch: 'Estirar',
    placementTile: 'Mosaico',
    designWorkspace: 'Espacio de diseño',
    designParameters: 'Parámetros de diseño',
    designTemplates: 'Plantillas de diseño',
    designTemplatesDescription: 'Ajustes reutilizables de tamaño de página, fuentes, encabezados y espaciado que los usuarios del proyecto pueden aplicar a un libro.',
    newDesignTemplate: 'Nueva plantilla',
    editDesignTemplate: 'Editar plantilla',
    createDesignTemplate: 'Crear plantilla',
    couldNotLoadDesignTemplates: 'No se pudieron cargar las plantillas de diseño.',
    couldNotSaveDesignTemplate: 'No se pudo guardar la plantilla de diseño.',
    designTemplateCreated: 'Plantilla de diseño creada.',
    designTemplateUpdated: 'Plantilla de diseño actualizada.',
    noDesignTemplatesYet: 'Aún no hay plantillas de diseño.',
    templateNameRequired: 'El nombre de la plantilla es obligatorio.',
    bookType: 'Tipo de libro',
    basicInfo: 'Información básica',
    indentationAndRules: 'Sangría y reglas',
    saveChanges: 'Guardar cambios',
    archive: 'Archivar',
    restore: 'Restaurar',
    archived: 'Archivado',
    edit: 'Editar',
    duplicate: 'Duplicar',
    copyNoun: 'copia',
    headers: 'Encabezados',
    picture: 'Ilustrado',
    mixed: 'Mixto',
    applyTemplate: 'Aplicar plantilla',
    applyDesignTemplate: 'Aplicar plantilla de diseño',
    selectTemplate: 'Seleccionar plantilla',
    noDesignTemplatesAvailable: 'Aún no hay plantillas de diseño disponibles.',
    applyingTemplateReplacesSettings: 'Aplicar una plantilla reemplaza los ajustes actuales de impresión, fuentes y espaciado con los de la plantilla. No se guarda automáticamente: revise y aplique desde los diálogos de parámetros, o use Guardar libro, para conservarlos.',
    templateApplied: 'Plantilla aplicada. Revise los ajustes y guarde para conservarlos.',
    bookDesignDrafts: 'Diseños del libro',
    activeBookDesignDraft: 'Diseño activo',
    noActiveBookDesignDraft: 'No hay diseño activo',
    selectOrCreateBookDesignDraft: 'Seleccione un diseño de libro o cree uno nuevo antes de editar el diseño.',
    createBookDesignDraft: 'Crear diseño',
    bookDesignDraftName: 'Nombre del diseño',
    bookDesignDraftNameRequired: 'El nombre del diseño es obligatorio.',
    couldNotCreateBookDesignDraft: 'No se pudo crear el diseño del libro.',
    couldNotDeleteBookDesignDraft: 'No se pudo eliminar el diseño del libro.',
    designTemplate: 'Plantilla de diseño',
    noTemplate: 'Sin plantilla',
    creatingUserRecordedFromSession: 'El usuario creador se registrará desde la sesión actual.',
    selectBookDesignDraft: 'Seleccionar diseño',
    noBookDesignDraftsYet: 'Aún no hay diseños de libro.',
    deleteBookDesignDraft: 'Eliminar diseño del libro',
    bookDetails: 'Detalles del libro',
    coverDesign: 'Diseño de portada',
    startDesignDraft: 'Iniciar diseño',
    noDesignArtifactsYet: 'Aún no hay materiales de diseño.',
    parameter: 'Parámetro',
    value: 'Valor',
    unit: 'Unidad',
    cm: 'cm',
    element: 'Elemento',
    lineHeight: 'Interlineado',
    style: 'Estilo',
    paragraph: 'Párrafo',
    photos: 'Fotos',
    cover: 'Portada',
    printParameters: 'Parámetros de impresión',
    fonts: 'Fuentes',
    indentations: 'Sangrías',
    rules: 'Reglas',
    typography: 'Tipografía',
    visuals: 'Visuales',
    rendererPreview: 'Vista previa',
    noExactPreviewYet: 'Aún no hay vista previa exacta',
    exactPreviewRequiresRenderer: 'Actualiza la vista previa del renderizador para ver el mismo artefacto que se usará para el PDF.',
    loadingPreview: 'Cargando vista previa...',
    couldNotLoadPdfPreview: 'No se pudo cargar la vista previa del PDF.',
    previewMode: 'Modo de vista previa',
    singlePage: 'Página individual',
    twoPageSpread: 'Doble página',
    zoomIn: 'Acercar',
    zoomOut: 'Alejar',
    resetZoomToFit: 'Ajustar al marco',
    actualSize: 'Tamaño real (clic para 100%)',
    production: 'Producción',
    runPreflight: 'Ejecutar preflight',
    sendToPrinting: 'Enviar a impresión',
    publishingNotes: 'Notas de publicación',
    printProvider: 'Proveedor de impresión',
    submissionTracker: 'Seguimiento de envío',
    history: 'Historial',
    title: 'Título',
    subtitle: 'Subtítulo',
    authorName: 'Nombre del autor',
    direction: 'Dirección',
    manuscriptText: 'Texto del manuscrito',
    searchManuscript: 'Buscar en manuscrito',
    draft: 'Borrador',
    intake: 'Ingreso',
    interview_planned: 'Entrevista planificada',
    interview_in_progress: 'Entrevista en curso',
    transcription_in_progress: 'Transcripción en curso',
    story_outline_in_progress: 'Esquema en curso',
    story_writing_in_progress: 'Redacción en curso',
    review_in_progress: 'Revisión en curso',
    design_in_progress: 'Diseño en curso',
    approval_pending: 'Pendiente de aprobación',
    publishing_in_progress: 'Publicación en curso',
    completed: 'Completado',
    on_hold: 'En pausa',
    cancelled: 'Cancelado',
    package_ready: 'Paquete listo',
    changes_requested: 'Cambios solicitados',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    active: 'Activo',
    resolved: 'Resuelto',
    openStatus: 'Abierto',
    deactivated: 'Desactivado',
    publishing_ready: 'Listo para publicar',
    design_ready: 'Listo para diseño',
    passed: 'Aprobado',
    failed: 'Fallido',
    warning: 'Advertencia',
    life_book: 'Libro de vida o legado',
    family_book: 'Libro familiar',
    community_book: 'Libro comunitario',
    cookbook: 'Recetario',
    picture_book: 'Libro ilustrado',
    video_clip: 'Video corto',
    presentation: 'Presentación',
    self_service: 'Autoservicio',
    guided: 'Guiado',
    full_service: 'Servicio completo',
    project_manager: 'Gestor del proyecto',
    supervisor: 'Supervisor',
    service_provider: 'Proveedor de servicio',
    writer: 'Redactor',
    designer: 'Diseñador',
    reviewer: 'Revisor',
    amazon_kdp: 'Amazon KDP',
    other_provider: 'Otro proveedor',
    print_pdf: 'PDF de impresión',
    proof_pdf: 'PDF de prueba',
    renderer_preview: 'Vista previa',
    draft_design: 'Diseño borrador',
    approved_for_export: 'Diseño aprobado',
    layout_review: 'Revisión de maquetación',
    story_chapter: 'Capítulo',
    regular_text: 'Texto normal',
    chapter_title: 'Título de capítulo',
    sub_chapter: 'Subcapítulo',
    sub_sub_chapter: 'Sub-subcapítulo',
    quoted_text: 'Cita',
    after_paragraph: 'Después del párrafo',
    top_of_next_page: 'Arriba de la página',
    full_page: 'Página completa',
    absolute: 'Posicionado en página',
    next: 'Siguiente',
    left: 'Izquierda',
    right: 'Derecha',
    center: 'Centro',
    outer: 'Exterior',
    inner: 'Interior',
    justify: 'Justificar',
    front: 'Frente',
    back: 'Dorso',
    photo: 'Foto',
    text: 'Texto',
    grayscale: 'Escala de grises',
    sepia: 'Sepia',
    page: 'Página',
    check: 'Verificación',
    createBook: 'Crear libro',
    matches: 'Coincidencias',
    placed: 'colocadas',
    items: 'elementos',
    anchor: 'Ancla',
    placement: 'Ubicación',
    pageSide: 'Lado de página',
    align: 'Alinear',
    rotate: 'Rotar',
    filter: 'Filtro',
    cropTopPercent: 'Recorte superior %',
    cropRightPercent: 'Recorte derecho %',
    cropBottomPercent: 'Recorte inferior %',
    cropLeftPercent: 'Recorte izquierdo %',
    caption: 'Pie de foto',
    addPhoto: 'Agregar foto',
    layout: 'Diseño',
    movePhotoToCurrentPosition: 'Mover foto a la posición actual',
    removePhoto: 'Quitar foto',
    noPhotosPlacedYet: 'Aún no hay fotos colocadas.',
    side: 'Lado',
    item: 'Elemento',
    position: 'Posición',
    coverText: 'Texto de portada',
    coverBackgroundFront: 'Color de fondo frontal',
    coverBackgroundBack: 'Color de fondo trasero',
    coverDefaultFont: 'Predeterminada del tema',
    addCover: 'Agregar portada',
    moveCoverItemToCurrentPosition: 'Mover elemento de portada a la posición actual',
    removeCoverItem: 'Quitar elemento de portada',
    noCoverItemsYet: 'Aún no hay elementos de portada.',
    widthCm: 'Ancho',
    heightCm: 'Alto',
    textAlign: 'Alineación de texto',
    startPage: 'Página inicial',
    innerMarginCm: 'Margen interior',
    outerMarginCm: 'Margen exterior',
    topMarginCm: 'Margen superior',
    bottomMarginCm: 'Margen inferior',
    rightPageHeader: 'Encabezado página derecha',
    leftPageHeader: 'Encabezado página izquierda',
    headerTokensHint: 'Tokens: {page_number}, {book_title}, {author_name}, {chapter_title}',
    rightHeaderTopCm: 'Encabezado derecho superior',
    leftHeaderTopCm: 'Encabezado izquierdo superior',
    rightHeaderAlign: 'Alinear encabezado derecho',
    leftHeaderAlign: 'Alinear encabezado izquierdo',
    font: 'Fuente',
    size: 'Tamaño',
    color: 'Color',
    bold: 'Negrita',
    italic: 'Cursiva',
    leftToRight: 'Izquierda a derecha',
    rightToLeft: 'Derecha a izquierda',
    spacingAndRules: 'Espaciado y reglas',
    paragraphIndentCm: 'Sangría de párrafo',
    subParagraphCm: 'Subpárrafo',
    subSubParagraphCm: 'Sub-subpárrafo',
    chapterStart: 'Inicio de capítulo',
    contentIndex: 'Contenido',
    generateContentIndex: 'Generar contenido',
    contentIndexTitle: 'Título del contenido',
    includeSubChapters: 'Incluir subcapítulos',
    includeSubSubChapters: 'Incluir sub-subcapítulos',
    contentIndexDotLeaders: 'Puntos guía',
    leftPage: 'Página izquierda',
    rightPage: 'Página derecha',
    previousPage: 'Página anterior',
    nextPage: 'Página siguiente',
    chapterAboveCm: 'Capítulo arriba',
    chapterBelowCm: 'Capítulo abajo',
    subChapterAboveCm: 'Subcapítulo arriba',
    subChapterBelowCm: 'Subcapítulo abajo',
    subSubAboveCm: 'Sub-sub arriba',
    subSubBelowCm: 'Sub-sub abajo',
    textAboveCm: 'Texto arriba',
    textBelowCm: 'Texto abajo',
    paragraphEndMarker: 'Marcador de fin de párrafo',
    marker: 'Marcador',
    markerOffsetCm: 'Desplazamiento marcador',
    oneWord: 'Una palabra',
    oneLine: 'Una línea',
    pages: 'páginas',
    refreshPreview: 'Actualizar vista previa',
    renderedBookProof: 'Prueba renderizada del libro',
    untitledBook: 'Libro sin título',
    noManuscriptTextYet: 'Aún no hay texto de manuscrito.',
    saveBook: 'Guardar libro',
    applyAndRefreshPreview: 'Aplicar y actualizar vista previa',
    versionNote: 'Nota de versión',
    saveVersion: 'Guardar versión',
    approveBook: 'Aprobar libro',
    exportType: 'Tipo de exportación',
    requestExport: 'Solicitar exportación',
    export: 'Exportación',
    failedCount: 'fallidas',
    warningsCount: 'advertencias',
    message: 'Mensaje',
    noMessageRecorded: 'No hay mensaje registrado.',
    noPreflightReport: 'No se registró informe de preflight para esta exportación.',
    noExportReadyForPreflight: 'No hay exportación lista para preflight.',
    marketplace: 'Marketplace',
    reviewerNotes: 'Notas del revisor',
    interior: 'Interior',
    save: 'Guardar',
    accept: 'Aceptar',
    acceptAsNewChapter: 'Aceptar como capítulo nuevo',
    acceptedGeneratedChapter: 'Capítulo generado aceptado',
    ai: 'IA',
    aiChapterFromSources: 'Capítulo con IA desde fuentes',
    ai_draft: 'Borrador de IA',
    appendToEmptyDraft: 'Agregar a un borrador vacío',
    approve: 'Aprobar',
    approveStoryChapter: 'Aprobar capítulo',
    approveStoryOutline: 'Aprobar esquema',
    approvedOutline: 'Esquema aprobado',
    approvedStoryOutlineRequired: 'Se requiere un esquema aprobado.',
    approvedTranscript: 'Transcripción aprobada',
    approvedTranscriptRequired: 'Se requiere una transcripción aprobada.',
    artifact: 'Material',
    howAdded: 'Cómo se agregó',
    creatingUser: 'Usuario creador',
    current: 'Actual',
    nameDescription: 'Nombre / Descripción',
    commonParameters: 'Parámetros comunes',
    fileDetails: 'Detalles del archivo',
    openArtifactFile: 'Abrir archivo del material',
    openExternalSource: 'Abrir fuente externa',
    noPreviewAvailable: 'No hay vista previa disponible.',
    uploaded: 'Subido',
    linked: 'Vinculado',
    close: 'Cerrar',
    artifactTitleRequired: 'El título del material es obligatorio.',
    asin: 'ASIN',
    askForEditOrInsertion: 'Pide una edición o inserción',
    assistant: 'Asistente',
    author: 'Autor',
    bookTitleRequired: 'El título del libro es obligatorio.',
    changeNote: 'Nota de cambio',
    changes: 'Cambios',
    chapter: 'Capítulo',
    book_chapter: 'Capítulo',
    book_outline: 'Esquema',
    preface: 'Prefacio',
    acknowledgement: 'Agradecimientos',
    introduction: 'Introducción',
    afterword: 'Epílogo',
    custom: 'Personalizado',
    chapterBody: 'Contenido del capítulo',
    chapterTitle: 'Título del capítulo',
    chat: 'Chat',
    chooseRecording: 'Elegir grabación',
    chooseRecordingBeforeGeneratingTranscript: 'Elige una grabación antes de generar una transcripción.',
    chooseTranscriptFile: 'Elegir archivo de transcripción',
    chooseSessionAndRecordingFileFirst: 'Elige una sesión y un archivo de grabación primero.',
    comment: 'Comentario',
    comments: 'Comentarios',
    couldNotAcceptGeneratedChapter: 'No se pudo aceptar el capítulo generado.',
    couldNotAddReviewComment: 'No se pudo agregar el comentario de revisión.',
    couldNotApproveBookResolveComments: 'No se pudo aprobar el libro. Resuelve primero los comentarios abiertos.',
    couldNotApproveStoryChapter: 'No se pudo aprobar el capítulo.',
    couldNotApproveStoryOutline: 'No se pudo aprobar el esquema.',
    couldNotApproveTranscript: 'No se pudo aprobar la transcripción.',
    couldNotAssignProjectMember: 'No se pudo asignar el miembro al proyecto.',
    couldNotCheckServiceStatus: 'No se pudo comprobar el estado del servicio.',
    couldNotCreateBook: 'No se pudo crear el libro.',
    couldNotCreateInterviewSession: 'No se pudo crear la sesión de entrevista.',
    couldNotCreateProject: 'No se pudo crear el proyecto.',
    couldNotCreateStoryArtifact: 'No se pudo crear el material de historia.',
    couldNotGenerateChapterFromSelectedSources: 'No se pudo generar un capítulo desde las fuentes seleccionadas.',
    couldNotGenerateStoryChapters: 'No se pudieron generar capítulos.',
    couldNotGenerateStoryOutline: 'No se pudo generar el esquema.',
    couldNotGenerateTranscript: 'No se pudo generar la transcripción.',
    couldNotLoadBookWorkflow: 'No se pudo cargar el flujo del libro.',
    couldNotLoadInterviewStudio: 'No se pudo cargar el Estudio de entrevistas.',
    couldNotLoadProjectWorkspace: 'No se pudo cargar el espacio del proyecto.',
    couldNotLoadProjectWorkspaceData: 'No se pudieron cargar los datos del espacio del proyecto.',
    couldNotLoadPublishingRequests: 'No se pudieron cargar las solicitudes de publicación.',
    couldNotLoadWritingStudio: 'No se pudo cargar el Estudio de escritura.',
    couldNotPrepareTranslationSuggestions: 'No se pudieron preparar las sugerencias de traducción.',
    couldNotReadTranscriptFile: 'No se pudo leer el archivo de transcripción.',
    couldNotRecordReviewDecision: 'No se pudo registrar la decisión de revisión.',
    couldNotRefineTranscript: 'No se pudo refinar la transcripción.',
    couldNotRefreshRendererPreview: 'No se pudo actualizar la vista previa.',
    couldNotRequestExportApproveBook: 'No se pudo solicitar la exportación. Aprueba primero el libro.',
    couldNotRequestReview: 'No se pudo solicitar la revisión.',
    couldNotRunExportPreflight: 'No se pudo ejecutar el preflight de exportación.',
    couldNotSaveArtifact: 'No se pudo guardar el material.',
    couldNotSaveBookNeedsDraft: 'No se pudo guardar el libro. Se requiere un borrador del manuscrito.',
    bookLockedForEditing: 'Este diseño del libro no se puede editar en su estado actual. Los cambios no se guardaron y la vista previa no se actualizó.',
    couldNotSaveBookVersion: 'No se pudo guardar la versión del libro.',
    couldNotSaveProject: 'No se pudo guardar el proyecto.',
    couldNotSaveStoryArtifactVersion: 'No se pudo guardar la versión del material de historia.',
    couldNotSaveStoryChapter: 'No se pudo guardar el capítulo.',
    couldNotSaveStoryOutline: 'No se pudo guardar el esquema.',
    couldNotSaveTranscript: 'No se pudo guardar la transcripción.',
    couldNotSaveZoomMeetingLink: 'No se pudo guardar el enlace de Zoom.',
    couldNotSendExportToPrinting: 'No se pudo enviar la exportación a impresión.',
    couldNotSendStoryDocumentToDesign: 'No se pudo enviar el documento de historia a diseño.',
    couldNotSendStoryDocumentToPublishing: 'No se pudo enviar el documento de historia a publicación.',
    couldNotStartReview: 'No se pudo iniciar la revisión.',
    couldNotUnassignProjectMember: 'No se pudo quitar el miembro del proyecto.',
    couldNotUpdatePublishingSubmission: 'No se pudo actualizar el envío de publicación.',
    couldNotUpdateStoryDocument: 'No se pudo actualizar el documento de historia.',
    couldNotUploadRecording: 'No se pudo subir la grabación.',
    couldNotUploadStoryDocument: 'No se pudo subir el documento de historia.',
    createFirstStoryArtifactFromEditor: 'Crea el primer material de historia desde el editor.',
    createStoryArtifact: 'Crear material de historia',
    createdFromWritingStudio: 'Creado desde el Estudio de escritura',
    currentDraft: 'Borrador actual',
    currentStatus: 'Estado actual',
    currentTarget: 'Destino actual',
    decision: 'Decisión',
    decisionNote: 'Nota de decisión',
    description: 'Descripción',
    designReady: 'Listo para diseño',
    diffAcceptReject: 'Diferencias / aceptar / rechazar',
    dismiss: 'Descartar',
    dismissed: 'Descartado',
    editStoryArtifact: 'Editar material de historia',
    editStoryChapter: 'Editar capítulo',
    editStoryOutline: 'Editar esquema',
    editableStoryText: 'Texto de historia editable',
    empty: 'Vacío',
    end: 'Fin',
    enterStoryTextBeforeTranslating: 'Ingresa texto de historia antes de traducir.',
    exportFailedPreflightReviewReport: 'La exportación falló el preflight. Revisa el informe.',
    externalUrl: 'URL externa',
    file: 'Archivo',
    format: 'Formato',
    aiTranscriptRefinement: 'Refinamiento de transcripción con IA',
    generateChapter: 'Generar capítulo',
    generateTranscript: 'Generar transcripción',
    generatedByWriterAgent: 'Generado por el agente de escritura',
    generatedChapterAcceptedAsNewStoryArtifact: 'Capítulo generado aceptado como nuevo material de historia.',
    generatedDraft: 'Borrador generado',
    generationInstructions: 'Instrucciones de generación',
    heightPercent: 'Alto %',
    interviewStudioDescription: 'De sesión a grabación, transcripción y material fuente aprobado.',
    interviewStudioRecordingFor: 'Grabación del Estudio de entrevistas para {title}',
    interviewSession: 'Sesión de entrevista',
    interviewTitleAndIntervieweeRequired: 'El título de la entrevista y el entrevistado son obligatorios.',
    invalidProjectId: 'Id. de proyecto inválido.',
    isbn: 'ISBN',
    join: 'Unirse',
    loadProjectToUseInterviewStudio: 'Carga un proyecto para usar el Estudio de entrevistas.',
    ltr: 'LTR',
    manualInterviewStudioEdit: 'Edición manual del Estudio de entrevistas',
    manualStudioSave: 'Guardado manual del estudio',
    meetingLink: 'Enlace de reunión',
    method: 'Método',
    min: 'min',
    modifiable: 'Modificable',
    newChapter: 'Capítulo nuevo',
    newManualTranscript: 'Nueva transcripción manual',
    no: 'Sin',
    noArtifactsYet: 'Aún no hay materiales.',
    noDescription: 'Sin descripción',
    noFileSelected: 'No se seleccionó archivo',
    noPendingSuggestions: 'No hay sugerencias pendientes.',
    noPreview: 'Sin vista previa',
    noReviewCommentsYet: 'Aún no hay comentarios de revisión.',
    noReviewsRequestedYet: 'Aún no se solicitaron revisiones.',
    noSessionsYet: 'Aún no hay sesiones.',
    noStoryArtifactSelected: 'No hay material de historia seleccionado',
    noStoryDocumentsReadyYet: 'Aún no hay documentos de historia listos.',
    noStoryOutlinesYet: 'Aún no hay esquemas de historia.',
    noStudioVersionsYet: 'Aún no hay versiones del estudio.',
    noSummary: 'Sin resumen',
    noTextSourcesAvailableYet: 'Aún no hay fuentes de texto disponibles.',
    openReviews: 'Revisiones abiertas',
    operationsDescription: 'Monitorea el estado de los servicios de producción.',
    order: 'Orden',
    outline: 'Esquema',
    outlineJson: 'JSON del esquema',
    outlineTitle: 'Título del esquema',
    projectTitleAndLanguageRequired: 'El título del proyecto y el idioma son obligatorios.',
    projectTitleOwnerAndLanguageRequired: 'El título del proyecto, responsable e idioma son obligatorios.',
    proposedText: 'Texto propuesto',
    publish: 'Publicar',
    publishingReady: 'Listo para publicación',
    rangeOnly: 'Solo rango',
    recording: 'Grabación',
    recordingDescription: 'Sube audio/video después de una sesión de Zoom o desde una grabadora externa.',
    refineTranscript: 'Refinar transcripción con IA',
    refresh: 'Actualizar',
    refreshServiceHealth: 'Actualizar estado de servicios',
    refreshingWritingStudio: 'Actualizando Estudio de escritura...',
    reject: 'Rechazar',
    reopen: 'Reabrir',
    request: 'Solicitud',
    resolve: 'Resolver',
    restoreToEditor: 'Restaurar al editor',
    review: 'Revisión',
    reviewCommentStatusFilter: 'Filtro de estado de comentarios',
    reviewCommentsNeedAnchor: 'Los comentarios de revisión necesitan un ancla.',
    reviewCommentsYet: 'comentarios de revisión aún.',
    reviewInstructions: 'Instrucciones de revisión',
    reviewTarget: 'Destino de revisión',
    reviewTargetAndCommentRequired: 'El destino de revisión y el comentario son obligatorios.',
    reviewTargetRequired: 'El destino de revisión es obligatorio.',
    reviewTargetType: 'Tipo de destino',
    reviewTitle: 'Título de revisión',
    reviewable: 'Revisable',
    rtl: 'RTL',
    saveText: 'Guardar texto',
    saveZoom: 'Guardar Zoom',
    savedVersion: 'Versión {version} guardada.',
    sections: 'Secciones',
    select: 'Seleccionar',
    selectAtLeastOneSourceBeforeGenerating: 'Selecciona al menos una fuente antes de generar.',
    selectBookAndEnterTitle: 'Selecciona un libro e ingresa un título.',
    selectEditableStudioArtifactAndEnterTitle: 'Selecciona un material editable del estudio e ingresa un título.',
    selectProjectToOpenWritingStudio: 'Selecciona un proyecto para abrir el Estudio de escritura.',
    selectSession: 'Selecciona una sesión',
    selectStoryDocument: 'Seleccionar documento de historia',
    selectedSources: 'Fuentes seleccionadas',
    selectedText: 'Texto seleccionado',
    selectedTextOrTargetNote: 'Texto seleccionado o nota de destino',
    send: 'Enviar',
    sessions: 'Sesiones',
    scheduleInterview: 'Programar entrevista',
    sources: 'Fuentes',
    start: 'Iniciar',
    startTranslationDraft: 'Iniciar borrador de traducción',
    storyArtifactCreated: 'Material de historia creado.',
    storyArtifacts: 'Materiales de historia',
    storyChapter: 'Capítulo de historia',
    storyChapterTitleAndBodyRequired: 'El título y contenido del capítulo son obligatorios.',
    storyDocument: 'Documento de historia',
    storyDocuments: 'Documentos de historia',
    storyDocumentsRequireWordOrPdf: 'Los documentos de historia requieren un archivo Word o PDF.',
    storyDocumentUploaded: 'Documento de historia subido.',
    storyOutline: 'Esquema de historia',
    storyOutlineJsonMustBeValid: 'El JSON del esquema debe ser válido.',
    storyOutlineTitleRequired: 'El título del esquema es obligatorio.',
    storyPipelineItemCreateStudioArtifact: 'Este elemento viene del flujo de historia existente. Crea un material de estudio para editarlo y versionarlo manualmente aquí.',
    storyText: 'Texto de historia',
    suggestionAppliedSaveToCreateVersion: 'Sugerencia aplicada al borrador del editor. Guarda para crear una versión.',
    suggestions: 'Sugerencias',
    summary: 'Resumen',
    targetDirection: 'Dir. destino',
    targetType: 'Tipo de destino',
    textArtifact: 'Material de texto',
    textContent: 'Contenido de texto',
    titleRequired: 'El título es obligatorio.',
    total: 'Total',
    transcript: 'Transcripción',
    transcriptEditorDescription: 'Edita manualmente mientras reproduces la grabación. Cada guardado crea una versión.',
    transcriptSessionAndSourceArtifactRequired: 'La sesión y el material fuente son obligatorios.',
    transcriptSessionAndTextRequired: 'La sesión y el texto de transcripción son obligatorios.',
    transcriptTextRequired: 'El texto de la transcripción es obligatorio.',
    translationDraft: 'Borrador de traducción',
    translationDraftFromTranscript: 'Borrador de traducción desde transcripción #{id}',
    translationSuggestionsReady: 'Las sugerencias de traducción están listas para revisar.',
    unsavedManuscriptEditsVisible: 'Las ediciones no guardadas del manuscrito son visibles en la vista previa.',
    updated: 'Actualizado',
    upload: 'Subir',
    uploadArtifact: 'Subir material',
    uploadInterviewRecording: 'Subir grabación de entrevista',
    uploadRecording: 'Subir grabación',
    uploadStoryDocument: 'Subir documento de historia',
    uploadTranscript: 'Subir transcripción',
    uploader: 'Quien sube',
    versionHistory: 'Historial de versiones',
    versions: 'versiones',
    widthPercent: 'Ancho %',
    words: 'Palabras',
    writerChat: 'Chat de escritura',
    writerChatCouldNotRespond: 'El chat de escritura no pudo responder.',
    xPercent: 'X %',
    yPercent: 'Y %',
    zoom: 'Zoom',
    zoomLink: 'Enlace de Zoom',
    zoomMeetingLink: 'Enlace de reunión de Zoom',
    zoomMeetingLinkMustBeHttps: 'El enlace de Zoom debe ser una URL HTTPS de zoom.us.',
    zoomMeetingLinkRequired: 'El enlace de Zoom es obligatorio.',
  },
  he: {
    appSubtitle: 'סביבת הפקה',
    signInSubtitle: 'התחברות לסביבת ההפקה שלך.',
    email: 'אימייל',
    password: 'סיסמה',
    signIn: 'כניסה',
    signOut: 'יציאה',
    loadingWorkspace: 'טוען סביבת עבודה...',
    primaryNavigation: 'ניווט ראשי',
    invalidEmailOrPassword: 'אימייל או סיסמה שגויים.',
    projects: 'פרויקטים',
    projectPortal: 'פורטל פרויקטים',
    adminConsole: 'מסוף ניהול',
    adminProjects: 'ניהול פרויקטים',
    adminProjectsDescription: 'פיקוח על יצירת פרויקטים, בעלות, גישה וסטטוס הפקה בכל המערכת.',
    publishing: 'הוצאה לאור',
    users: 'משתמשים',
    user: 'משתמש',
    usersAndAccess: 'משתמשים והרשאות',
    usersAndAccessDescription: 'יצירת משתמשי מערכת ושמירת סטטוס הגישה גלוי לפני שיוכם לפרויקטים.',
    refreshUsers: 'רענון משתמשים',
    newUser: 'משתמש חדש',
    createUser: 'יצירת משתמש',
    createdUser: 'נוצר משתמש',
    couldNotLoadUsers: 'לא ניתן לטעון משתמשים.',
    couldNotCreateUser: 'לא ניתן ליצור משתמש.',
    couldNotUpdateUserStatus: 'לא ניתן לעדכן סטטוס משתמש.',
    username: 'שם משתמש',
    displayName: 'שם לתצוגה',
    phoneNumber: 'מספר טלפון',
    temporaryPassword: 'סיסמה זמנית',
    temporaryPasswordHelper: 'יש להשתמש בלפחות 8 תווים, או להשאיר ריק.',
    temporaryPasswordMinLength: 'סיסמה זמנית חייבת להכיל לפחות 8 תווים.',
    emailRequired: 'חובה להזין אימייל.',
    enterEmailAddress: 'יש להזין כתובת אימייל.',
    uiLanguage: 'שפת ממשק',
    scope: 'תחום גישה',
    admin: 'מנהל מערכת',
    projectScoped: 'גישה לפי פרויקטים',
    deactivateUser: 'השבתת משתמש',
    activateUser: 'הפעלת משתמש',
    noUsersYet: 'אין עדיין משתמשים.',
    creating: 'יוצר...',
    audit: 'ביקורת',
    auditTrail: 'יומן ביקורת',
    auditTrailDescription: 'סקירת יצירת פרויקטים, שינויי צוות, שינויי סטטוס והיסטוריית אישורים.',
    couldNotLoadProjects: 'לא ניתן לטעון פרויקטים.',
    couldNotLoadAuditEvents: 'לא ניתן לטעון אירועי ביקורת.',
    openProject: 'פתיחת פרויקט',
    time: 'זמן',
    action: 'פעולה',
    target: 'יעד',
    actor: 'מבצע',
    noAuditEventsForProject: 'אין אירועי ביקורת לפרויקט זה.',
    noProjectAuditEvents: 'אין אירועי ביקורת לפרויקט.',
    operations: 'תפעול',
    projectWorkspace: 'סביבת פרויקטים',
    projectWorkspaceDescription: 'ניהול תהליך ההפקה סביב פרויקטים, אנשים, שפות, אישורים וביקורת.',
    refreshProjects: 'רענון פרויקטים',
    newProject: 'פרויקט חדש',
    createUserFirst: 'יש ליצור משתמש לפני פתיחת הפרויקט הראשון.',
    activeProjects: 'פרויקטים פעילים',
    inReview: 'בבדיקה',
    pendingApproval: 'ממתין לאישור',
    project: 'פרויקט',
    status: 'סטטוס',
    storyType: 'סוג סיפור',
    language: 'שפה',
    owner: 'אחראי',
    role: 'תפקיד',
    actions: 'פעולות',
    assign: 'שיוך',
    assignTeam: 'שיוך צוות',
    unassign: 'הסרה',
    noProductionNotes: 'אין עדיין הערות הפקה',
    unassigned: 'לא משויך',
    open: 'פתיחה',
    noProjectsYet: 'אין עדיין פרויקטים.',
    createProject: 'יצירת פרויקט',
    projectName: 'שם הפרויקט',
    goalsAndInstructions: 'מטרות והנחיות',
    projectManager: 'מנהל פרויקט',
    package: 'חבילה',
    primaryLanguage: 'שפה ראשית',
    cancel: 'ביטול',
    create: 'יצירה',
    saveProject: 'שמירת פרויקט',
    publishingDashboard: 'לוח הוצאה לאור',
    publishingDashboardDescription: 'מעקב אחר חבילות KDP והגשות הוצאה לאור בכל הפרויקטים הפעילים.',
    refreshPublishingRequests: 'רענון בקשות הוצאה לאור',
    totalRequests: 'סה״כ בקשות',
    ready: 'מוכן',
    submitted: 'הוגש',
    published: 'פורסם',
    allStatuses: 'כל הסטטוסים',
    provider: 'ספק',
    allProviders: 'כל הספקים',
    book: 'ספר',
    submittedDate: 'הוגש',
    reference: 'אסמכתא',
    none: 'אין',
    notRecorded: 'לא נרשם',
    noMarketplace: 'אין חנות',
    loadingPublishingRequests: 'טוען בקשות הוצאה לאור...',
    noPublishingRequestsMatch: 'אין בקשות הוצאה לאור שמתאימות למסננים.',
    packageReady: 'חבילה',
    preflight: 'בדיקת קדם',
    settings: 'הגדרות',
    assets: 'חומרים',
    interviewsAndTranscripts: 'ראיונות ותמלולים',
    interviewStudio: 'סטודיו ראיונות',
    storyStudio: 'סטודיו סיפור',
    storyOutlines: 'מבני סיפור',
    storyFiles: 'קבצי סיפור',
    chapters: 'פרקים',
    targetLanguage: 'שפת יעד',
    textDirection: 'כיוון טקסט',
    translateDraft: 'תרגום טיוטה',
    translation: 'תרגום',
    overview: 'סקירה',
    team: 'צוות',
    loadingProject: 'טוען פרויקט...',
    refreshProject: 'רענון פרויקט',
    projectSnapshot: 'תמונת מצב',
    interviewSessions: 'ראיונות',
    transcripts: 'תמלולים',
    interviewTitle: 'כותרת ראיון',
    type: 'סוג',
    audio: 'אודיו',
    video: 'וידאו',
    story_document: 'מסמך סיפור',
    document: 'מסמך',
    other: 'אחר',
    interviewer: 'מראיין',
    interviewee: 'מרואיין',
    scheduled: 'מתוזמן',
    notScheduled: 'לא תוזמן',
    minutes: 'דקות',
    notes: 'הערות',
    add: 'הוספה',
    update: 'עדכון',
    generate: 'יצירה',
    session: 'מפגש',
    participants: 'משתתפים',
    duration: 'משך',
    pending: 'ממתין',
    saved: 'נשמר',
    noInterviewSessionsYet: 'אין עדיין מפגשי ראיון.',
    sourceArtifact: 'חומר מקור',
    confidence: 'ודאות',
    transcriptText: 'טקסט תמלול',
    source: 'מקור',
    created: 'נוצר',
    manualTranscript: 'תמלול ידני',
    editTranscript: 'עריכת תמלול',
    approveTranscript: 'אישור תמלול',
    noTranscriptsYet: 'אין עדיין תמלולים.',
    artifacts: 'חומרים',
    design: 'עיצוב',
    interviews: 'ראיונות',
    publishingStage: 'הוצאה לאור',
    reviews: 'ביקורות',
    story: 'סיפור',
    workflow: 'תהליך',
    notSet: 'לא הוגדר',
    bookContent: 'תוכן הספר',
    content: 'תוכן',
    image: 'תמונה',
    spacer: 'רווח',
    pageBreak: 'מעבר עמוד',
    subHeading: 'כותרת משנה',
    subChapter: 'תת-פרק',
    subSubChapter: 'תת-תת-פרק',
    quotedText: 'טקסט מצוטט',
    bodyText: 'טקסט רגיל',
    alignment: 'יישור',
    moveUp: 'העבר למעלה',
    moveDown: 'העבר למטה',
    addSection: 'הוסף מקטע',
    sectionTitle: 'כותרת מקטע',
    titleLevel: 'רמת כותרת',
    startsOn: 'מתחיל ב',
    indentLevel: 'רמת הזחה',
    headingText: 'טקסט כותרת',
    headingLevel: 'רמת כותרת',
    paragraphText: 'טקסט פסקה',
    paragraphStyle: 'סגנון',
    addParagraph: 'הוסף פסקה',
    addSubChapter: 'הוסף תת-פרק',
    addSubSubChapter: 'הוסף תת-תת-פרק',
    deleteHeading: 'מחק כותרת',
    deleteParagraph: 'מחק פסקה',
    mergeParagraph: 'מזג עם הפסקה הבאה',
    imageLayout: 'פריסה',
    layoutInline: 'בתוך השורה',
    layoutFloat: 'צף',
    layoutFullWidth: 'רוחב מלא',
    layoutFullPage: 'עמוד מלא',
    layoutFullBleed: 'עד הקצה',
    sideNext: 'עמוד הבא',
    sideLeft: 'עמוד שמאלי',
    sideRight: 'עמוד ימני',
    untitledSection: 'מקטע ללא כותרת',
    fixedPages: 'עמודים קבועים',
    fixedPage: 'עמוד קבוע',
    fixedSpread: 'כפולה קבועה',
    noSectionsYet: 'אין עדיין מקטעים. הוסף אחד כדי להתחיל.',
    sectionEmpty: 'המקטע ריק. הוסף בלוק למטה.',
    dragImagesHint: 'גרור תמונה לתוך הטקסט כדי למקם אותה בין פסקאות.',
    dragImageToPlace: 'גרור לתוך הטקסט למיקום',
    dropImageHere: 'שחרר כאן את התמונה',
    removeSection: 'הסר מקטע',
    spacerHeightCm: 'גובה',
    breakToSide: 'מעבר אל',
    fixedUnitPreserved: 'פריסה קבועה — עריכה בעורך הקנבס (בקרוב). נשמר בשמירה.',
    blocks: 'בלוקים',
    collapse: 'כווץ',
    expand: 'הרחב',
    showMore: 'הצג עוד',
    addPage: 'הוסף עמוד',
    addSpread: 'הוסף כפולה',
    pageRole: 'תפקיד עמוד',
    roleContent: 'תוכן',
    roleFullBleedImage: 'תמונה עד הקצה',
    roleTitle: 'כותרת',
    rolePartDivider: 'מפריד חלק',
    textBlock: 'בלוק טקסט',
    imageBlock: 'בלוק תמונה',
    noPhoto: 'אין תמונה',
    background: 'רקע',
    backgroundType: 'סוג רקע',
    backgroundImage: 'תמונת רקע',
    fit: 'התאמה',
    fitCover: 'כיסוי',
    fitContain: 'הכלה',
    focalX: 'מוקד X %',
    focalY: 'מוקד Y %',
    selectedBlock: 'בלוק נבחר',
    canvasEmpty: 'עמוד ריק. הוסף בלוק תמונה או טקסט.',
    selectBlockHint: 'בחר בלוק בקנבס כדי לערוך אותו.',
    removeBlock: 'הסר בלוק',
    fontSize: 'גודל גופן (pt)',
    positionX: 'מיקום X %',
    positionY: 'מיקום Y %',
    rotation: 'סיבוב °',
    layer: 'שכבה (z)',
    imageFilter: 'מסנן',
    filterNone: 'ללא',
    filterGrayscale: 'גווני אפור',
    filterSepia: 'ספיה',
    onPage: 'בעמוד',
    bothPages: 'שני העמודים',
    frontMatter: 'עמודי פתיחה',
    bodyContent: 'תוכן ראשי',
    backMatter: 'עמודי סיום',
    pageBackground: 'רקע עמוד',
    pageBackgroundHelp:
      'הגדר תמונת ברירת מחדל שתוצג מאחורי כל עמוד. השתמש בתמונה אחת לכל העמודים, או תמונות שונות לעמוד ימני ושמאלי.',
    enableDefaultBackground: 'הפעל רקע ברירת מחדל',
    backgroundScope: 'חל על',
    allPages: 'כל העמודים',
    differentSides: 'ימין/שמאל שונים',
    placementCover: 'כיסוי (מילוי, חיתוך)',
    placementContain: 'הכלה (התאמה)',
    placementStretch: 'מתיחה',
    placementTile: 'ריצוף',
    designWorkspace: 'סטודיו עיצוב',
    designParameters: 'פרמטרי עיצוב',
    designTemplates: 'תבניות עיצוב',
    designTemplatesDescription: 'הגדרות גודל עמוד, גופנים, כותרות עמוד ומרווחים לשימוש חוזר, שמשתמשי הפרויקט יכולים להחיל על ספר.',
    newDesignTemplate: 'תבנית חדשה',
    editDesignTemplate: 'עריכת תבנית',
    createDesignTemplate: 'יצירת תבנית',
    couldNotLoadDesignTemplates: 'לא ניתן היה לטעון תבניות עיצוב.',
    couldNotSaveDesignTemplate: 'לא ניתן היה לשמור את תבנית העיצוב.',
    designTemplateCreated: 'תבנית העיצוב נוצרה.',
    designTemplateUpdated: 'תבנית העיצוב עודכנה.',
    noDesignTemplatesYet: 'אין עדיין תבניות עיצוב.',
    templateNameRequired: 'נדרש שם לתבנית.',
    bookType: 'סוג ספר',
    basicInfo: 'מידע בסיסי',
    indentationAndRules: 'הזחות וכללים',
    saveChanges: 'שמירת שינויים',
    archive: 'העברה לארכיון',
    restore: 'שחזור',
    archived: 'בארכיון',
    edit: 'עריכה',
    duplicate: 'שכפול',
    copyNoun: 'עותק',
    headers: 'כותרות עמוד',
    picture: 'מאויר',
    mixed: 'מעורב',
    applyTemplate: 'החלת תבנית',
    applyDesignTemplate: 'החלת תבנית עיצוב',
    selectTemplate: 'בחירת תבנית',
    noDesignTemplatesAvailable: 'אין עדיין תבניות עיצוב זמינות.',
    applyingTemplateReplacesSettings: 'החלת תבנית מחליפה את הגדרות ההדפסה, הגופנים והמרווחים הנוכחיות באלו מהתבנית. השינוי אינו נשמר אוטומטית — יש לבדוק ולהחיל דרך דיאלוגי הפרמטרים, או להשתמש בשמירת ספר, כדי לשמר אותו.',
    templateApplied: 'התבנית הוחלה. בדקו את ההגדרות ושמרו כדי לשמר אותן.',
    bookDesignDrafts: 'עיצובי ספר',
    activeBookDesignDraft: 'עיצוב פעיל',
    noActiveBookDesignDraft: 'אין עיצוב פעיל',
    selectOrCreateBookDesignDraft: 'בחרו עיצוב ספר קיים או צרו עיצוב חדש לפני עריכת העיצוב.',
    createBookDesignDraft: 'יצירת עיצוב',
    bookDesignDraftName: 'שם העיצוב',
    bookDesignDraftNameRequired: 'נדרש שם לעיצוב.',
    couldNotCreateBookDesignDraft: 'לא ניתן היה ליצור עיצוב ספר.',
    couldNotDeleteBookDesignDraft: 'לא ניתן היה למחוק את עיצוב הספר.',
    designTemplate: 'תבנית עיצוב',
    noTemplate: 'ללא תבנית',
    creatingUserRecordedFromSession: 'המשתמש היוצר יירשם לפי ההפעלה הנוכחית.',
    selectBookDesignDraft: 'בחירת עיצוב',
    noBookDesignDraftsYet: 'אין עדיין עיצובי ספר.',
    deleteBookDesignDraft: 'מחיקת עיצוב ספר',
    bookDetails: 'פרטי הספר',
    coverDesign: 'עיצוב כריכה',
    startDesignDraft: 'התחלת עיצוב',
    noDesignArtifactsYet: 'אין עדיין חומרי עיצוב.',
    parameter: 'פרמטר',
    value: 'ערך',
    unit: 'יחידה',
    cm: 'ס״מ',
    element: 'רכיב',
    lineHeight: 'גובה שורה',
    style: 'סגנון',
    paragraph: 'פסקה',
    photos: 'תמונות',
    cover: 'כריכה',
    printParameters: 'פרמטרי הדפסה',
    fonts: 'גופנים',
    indentations: 'הזחות',
    rules: 'כללים',
    typography: 'טיפוגרפיה',
    visuals: 'חזותי',
    rendererPreview: 'תצוגה מקדימה',
    noExactPreviewYet: 'אין עדיין תצוגה מקדימה מדויקת',
    exactPreviewRequiresRenderer: 'יש לרענן את תצוגת הרנדרר כדי לראות את אותו ארטיפקט שישמש להפקת ה-PDF.',
    loadingPreview: 'טוען תצוגה מקדימה...',
    couldNotLoadPdfPreview: 'לא ניתן לטעון תצוגת PDF מקדימה.',
    previewMode: 'מצב תצוגה',
    singlePage: 'עמוד יחיד',
    twoPageSpread: 'כפולת עמודים',
    zoomIn: 'הגדלה',
    zoomOut: 'הקטנה',
    resetZoomToFit: 'התאמה למסגרת',
    actualSize: 'גודל אמיתי (לחצו לזום 100%)',
    production: 'הפקה',
    runPreflight: 'הרצת בדיקת קדם',
    sendToPrinting: 'שליחה להדפסה',
    publishingNotes: 'הערות הוצאה לאור',
    printProvider: 'ספק הדפסה',
    submissionTracker: 'מעקב הגשה',
    history: 'היסטוריה',
    title: 'כותרת',
    subtitle: 'כותרת משנה',
    authorName: 'שם המחבר',
    direction: 'כיוון',
    manuscriptText: 'טקסט כתב היד',
    searchManuscript: 'חיפוש בכתב היד',
    draft: 'טיוטה',
    intake: 'קליטה',
    interview_planned: 'ראיון מתוכנן',
    interview_in_progress: 'ראיון בתהליך',
    transcription_in_progress: 'תמלול בתהליך',
    story_outline_in_progress: 'מתווה סיפור בתהליך',
    story_writing_in_progress: 'כתיבת סיפור בתהליך',
    review_in_progress: 'סקירה בתהליך',
    design_in_progress: 'עיצוב בתהליך',
    approval_pending: 'ממתין לאישור',
    publishing_in_progress: 'הוצאה לאור בתהליך',
    completed: 'הושלם',
    on_hold: 'בהמתנה',
    cancelled: 'בוטל',
    package_ready: 'חבילה מוכנה',
    changes_requested: 'נדרשים שינויים',
    approved: 'מאושר',
    rejected: 'נדחה',
    active: 'פעיל',
    resolved: 'טופל',
    openStatus: 'פתוח',
    deactivated: 'מושבת',
    publishing_ready: 'מוכן לפרסום',
    design_ready: 'מוכן לעיצוב',
    passed: 'עבר',
    failed: 'נכשל',
    warning: 'אזהרה',
    life_book: 'ספר חיים או מורשת',
    family_book: 'ספר משפחה',
    community_book: 'ספר קהילה',
    cookbook: 'ספר מתכונים',
    picture_book: 'ספר תמונות',
    video_clip: 'קליפ וידאו',
    presentation: 'מצגת',
    self_service: 'שירות עצמי',
    guided: 'מודרך',
    full_service: 'שירות מלא',
    project_manager: 'מנהל פרויקט',
    supervisor: 'מפקח',
    service_provider: 'ספק שירות',
    writer: 'כותב',
    designer: 'מעצב',
    reviewer: 'בודק',
    amazon_kdp: 'Amazon KDP',
    other_provider: 'ספק אחר',
    print_pdf: 'PDF להדפסה',
    proof_pdf: 'PDF להגהה',
    renderer_preview: 'תצוגה מקדימה',
    draft_design: 'טיוטת עיצוב',
    approved_for_export: 'עיצוב מאושר',
    layout_review: 'בדיקת עימוד',
    story_chapter: 'פרק',
    regular_text: 'טקסט רגיל',
    chapter_title: 'כותרת פרק',
    sub_chapter: 'תת פרק',
    sub_sub_chapter: 'תת-תת פרק',
    quoted_text: 'ציטוט',
    after_paragraph: 'אחרי פסקה',
    top_of_next_page: 'בראש העמוד',
    full_page: 'עמוד מלא',
    absolute: 'מיקום בעמוד',
    next: 'הבא',
    left: 'שמאל',
    right: 'ימין',
    center: 'מרכז',
    outer: 'חיצוני',
    inner: 'פנימי',
    justify: 'יישור מלא',
    front: 'קדמי',
    back: 'אחורי',
    photo: 'תמונה',
    text: 'טקסט',
    grayscale: 'גווני אפור',
    sepia: 'ספיה',
    page: 'עמוד',
    check: 'בדיקה',
    createBook: 'יצירת ספר',
    matches: 'התאמות',
    placed: 'ממוקמות',
    items: 'פריטים',
    anchor: 'עוגן',
    placement: 'מיקום',
    pageSide: 'צד עמוד',
    align: 'יישור',
    rotate: 'סיבוב',
    filter: 'מסנן',
    cropTopPercent: 'חיתוך עליון %',
    cropRightPercent: 'חיתוך ימני %',
    cropBottomPercent: 'חיתוך תחתון %',
    cropLeftPercent: 'חיתוך שמאלי %',
    caption: 'כיתוב',
    addPhoto: 'הוספת תמונה',
    layout: 'פריסה',
    movePhotoToCurrentPosition: 'העברת תמונה למיקום הנוכחי',
    removePhoto: 'הסרת תמונה',
    noPhotosPlacedYet: 'אין עדיין תמונות ממוקמות.',
    side: 'צד',
    item: 'פריט',
    position: 'מיקום',
    coverText: 'טקסט כריכה',
    coverBackgroundFront: 'צבע רקע קדמי',
    coverBackgroundBack: 'צבע רקע אחורי',
    coverDefaultFont: 'ברירת מחדל של העיצוב',
    addCover: 'הוספת כריכה',
    moveCoverItemToCurrentPosition: 'העברת פריט כריכה למיקום הנוכחי',
    removeCoverItem: 'הסרת פריט כריכה',
    noCoverItemsYet: 'אין עדיין פריטי כריכה.',
    widthCm: 'רוחב',
    heightCm: 'גובה',
    textAlign: 'יישור טקסט',
    startPage: 'עמוד התחלה',
    innerMarginCm: 'שוליים פנימיים',
    outerMarginCm: 'שוליים חיצוניים',
    topMarginCm: 'שוליים עליונים',
    bottomMarginCm: 'שוליים תחתונים',
    rightPageHeader: 'כותרת עמוד ימין',
    leftPageHeader: 'כותרת עמוד שמאל',
    headerTokensHint: 'תגים: {page_number}, {book_title}, {author_name}, {chapter_title}',
    rightHeaderTopCm: 'כותרת ימין מלמעלה',
    leftHeaderTopCm: 'כותרת שמאל מלמעלה',
    rightHeaderAlign: 'יישור כותרת ימין',
    leftHeaderAlign: 'יישור כותרת שמאל',
    font: 'גופן',
    size: 'גודל',
    color: 'צבע',
    bold: 'מודגש',
    italic: 'נטוי',
    leftToRight: 'משמאל לימין',
    rightToLeft: 'מימין לשמאל',
    spacingAndRules: 'ריווח וכללים',
    paragraphIndentCm: 'כניסת פסקה',
    subParagraphCm: 'תת פסקה',
    subSubParagraphCm: 'תת-תת פסקה',
    chapterStart: 'תחילת פרק',
    contentIndex: 'תוכן עניינים',
    generateContentIndex: 'יצירת תוכן עניינים',
    contentIndexTitle: 'כותרת תוכן עניינים',
    includeSubChapters: 'כלול תתי פרקים',
    includeSubSubChapters: 'כלול תתי-תתי פרקים',
    contentIndexDotLeaders: 'נקודות מובילות',
    leftPage: 'עמוד שמאל',
    rightPage: 'עמוד ימין',
    previousPage: 'עמוד קודם',
    nextPage: 'עמוד הבא',
    chapterAboveCm: 'רווח מעל פרק',
    chapterBelowCm: 'רווח מתחת פרק',
    subChapterAboveCm: 'רווח מעל תת פרק',
    subChapterBelowCm: 'רווח מתחת תת פרק',
    subSubAboveCm: 'רווח מעל תת-תת',
    subSubBelowCm: 'רווח מתחת תת-תת',
    textAboveCm: 'רווח מעל טקסט',
    textBelowCm: 'רווח מתחת טקסט',
    paragraphEndMarker: 'סמן סוף פסקה',
    marker: 'סמן',
    markerOffsetCm: 'היסט סמן',
    oneWord: 'מילה אחת',
    oneLine: 'שורה אחת',
    pages: 'עמודים',
    refreshPreview: 'רענון תצוגה',
    renderedBookProof: 'הוכחת ספר מרונדרת',
    untitledBook: 'ספר ללא כותרת',
    noManuscriptTextYet: 'אין עדיין טקסט כתב יד.',
    saveBook: 'שמירת ספר',
    applyAndRefreshPreview: 'החל ורענן תצוגה מקדימה',
    versionNote: 'הערת גרסה',
    saveVersion: 'שמירת גרסה',
    approveBook: 'אישור ספר',
    exportType: 'סוג יצוא',
    requestExport: 'בקשת יצוא',
    export: 'יצוא',
    failedCount: 'נכשלו',
    warningsCount: 'אזהרות',
    message: 'הודעה',
    noMessageRecorded: 'לא נרשמה הודעה.',
    noPreflightReport: 'לא נרשם דוח בדיקת קדם ליצוא הזה.',
    noExportReadyForPreflight: 'אין יצוא שמוכן לבדיקת קדם.',
    marketplace: 'חנות',
    reviewerNotes: 'הערות בודק',
    interior: 'פנים הספר',
    save: 'שמירה',
    accept: 'קבלה',
    acceptAsNewChapter: 'קבלה כפרק חדש',
    acceptedGeneratedChapter: 'פרק שנוצר התקבל',
    ai: 'בינה מלאכותית',
    aiChapterFromSources: 'פרק בינה ממקורות',
    ai_draft: 'טיוטת בינה',
    appendToEmptyDraft: 'הוספה לטיוטה ריקה',
    approve: 'אישור',
    approveStoryChapter: 'אישור פרק סיפור',
    approveStoryOutline: 'אישור מתווה סיפור',
    approvedOutline: 'מתווה מאושר',
    approvedStoryOutlineRequired: 'נדרש מתווה סיפור מאושר.',
    approvedTranscript: 'תמלול מאושר',
    approvedTranscriptRequired: 'נדרש תמלול מאושר.',
    artifact: 'חומר',
    howAdded: 'אופן הוספה',
    creatingUser: 'משתמש יוצר',
    current: 'נוכחי',
    nameDescription: 'שם / תיאור',
    commonParameters: 'פרמטרים נפוצים',
    fileDetails: 'פרטי קובץ',
    openArtifactFile: 'פתיחת קובץ החומר',
    openExternalSource: 'פתיחת מקור חיצוני',
    noPreviewAvailable: 'אין תצוגה מקדימה זמינה.',
    uploaded: 'הועלה',
    linked: 'קושר',
    close: 'סגירה',
    artifactTitleRequired: 'חובה להזין כותרת לחומר.',
    asin: 'ASIN',
    askForEditOrInsertion: 'בקשת עריכה או הוספה',
    assistant: 'מסייע',
    author: 'מחבר',
    bookTitleRequired: 'חובה להזין כותרת ספר.',
    changeNote: 'הערת שינוי',
    changes: 'שינויים',
    chapter: 'פרק',
    book_chapter: 'פרק',
    book_outline: 'מתווה',
    preface: 'הקדמה',
    acknowledgement: 'תודות',
    introduction: 'מבוא',
    afterword: 'אחרית דבר',
    custom: 'מותאם אישית',
    chapterBody: 'גוף הפרק',
    chapterTitle: 'כותרת פרק',
    chat: 'צ׳אט',
    chooseRecording: 'בחירת הקלטה',
    chooseRecordingBeforeGeneratingTranscript: 'יש לבחור הקלטה לפני יצירת תמלול.',
    chooseTranscriptFile: 'בחירת קובץ תמלול',
    chooseSessionAndRecordingFileFirst: 'יש לבחור מפגש וקובץ הקלטה קודם.',
    comment: 'תגובה',
    comments: 'תגובות',
    couldNotAcceptGeneratedChapter: 'לא ניתן לקבל את הפרק שנוצר.',
    couldNotAddReviewComment: 'לא ניתן להוסיף תגובת סקירה.',
    couldNotApproveBookResolveComments: 'לא ניתן לאשר את הספר. יש לטפל קודם בתגובות פתוחות.',
    couldNotApproveStoryChapter: 'לא ניתן לאשר פרק סיפור.',
    couldNotApproveStoryOutline: 'לא ניתן לאשר מתווה סיפור.',
    couldNotApproveTranscript: 'לא ניתן לאשר תמלול.',
    couldNotAssignProjectMember: 'לא ניתן לשייך חבר לפרויקט.',
    couldNotCheckServiceStatus: 'לא ניתן לבדוק סטטוס שירות.',
    couldNotCreateBook: 'לא ניתן ליצור ספר.',
    couldNotCreateInterviewSession: 'לא ניתן ליצור מפגש ראיון.',
    couldNotCreateProject: 'לא ניתן ליצור את הפרויקט.',
    couldNotCreateStoryArtifact: 'לא ניתן ליצור חומר סיפור.',
    couldNotGenerateChapterFromSelectedSources: 'לא ניתן ליצור פרק מהמקורות שנבחרו.',
    couldNotGenerateStoryChapters: 'לא ניתן ליצור פרקי סיפור.',
    couldNotGenerateStoryOutline: 'לא ניתן ליצור מתווה סיפור.',
    couldNotGenerateTranscript: 'לא ניתן ליצור תמלול.',
    couldNotLoadBookWorkflow: 'לא ניתן לטעון את תהליך הספר.',
    couldNotLoadInterviewStudio: 'לא ניתן לטעון את סטודיו הראיונות.',
    couldNotLoadProjectWorkspace: 'לא ניתן לטעון סביבת פרויקט.',
    couldNotLoadProjectWorkspaceData: 'לא ניתן לטעון נתוני סביבת פרויקט.',
    couldNotLoadPublishingRequests: 'לא ניתן לטעון בקשות הוצאה לאור.',
    couldNotLoadWritingStudio: 'לא ניתן לטעון את סטודיו הכתיבה.',
    couldNotPrepareTranslationSuggestions: 'לא ניתן להכין הצעות תרגום.',
    couldNotReadTranscriptFile: 'לא ניתן לקרוא את קובץ התמלול.',
    couldNotRecordReviewDecision: 'לא ניתן לרשום החלטת סקירה.',
    couldNotRefineTranscript: 'לא ניתן לשפר את התמלול.',
    couldNotRefreshRendererPreview: 'לא ניתן לרענן תצוגה מקדימה.',
    couldNotRequestExportApproveBook: 'לא ניתן לבקש יצוא. יש לאשר קודם את הספר.',
    couldNotRequestReview: 'לא ניתן לבקש סקירה.',
    couldNotRunExportPreflight: 'לא ניתן להריץ בדיקת קדם ליצוא.',
    couldNotSaveArtifact: 'לא ניתן לשמור חומר.',
    couldNotSaveBookNeedsDraft: 'לא ניתן לשמור ספר. נדרשת טיוטת כתב יד.',
    bookLockedForEditing: 'לא ניתן לערוך את עיצוב הספר במצבו הנוכחי. השינויים לא נשמרו והתצוגה המקדימה לא רועננה.',
    couldNotSaveBookVersion: 'לא ניתן לשמור גרסת ספר.',
    couldNotSaveProject: 'לא ניתן לשמור פרויקט.',
    couldNotSaveStoryArtifactVersion: 'לא ניתן לשמור גרסת חומר סיפור.',
    couldNotSaveStoryChapter: 'לא ניתן לשמור פרק סיפור.',
    couldNotSaveStoryOutline: 'לא ניתן לשמור מתווה סיפור.',
    couldNotSaveTranscript: 'לא ניתן לשמור תמלול.',
    couldNotSaveZoomMeetingLink: 'לא ניתן לשמור קישור Zoom.',
    couldNotSendExportToPrinting: 'לא ניתן לשלוח יצוא להדפסה.',
    couldNotSendStoryDocumentToDesign: 'לא ניתן לשלוח מסמך סיפור לעיצוב.',
    couldNotSendStoryDocumentToPublishing: 'לא ניתן לשלוח מסמך סיפור להוצאה לאור.',
    couldNotStartReview: 'לא ניתן להתחיל סקירה.',
    couldNotUnassignProjectMember: 'לא ניתן להסיר חבר מפרויקט.',
    couldNotUpdatePublishingSubmission: 'לא ניתן לעדכן הגשת הוצאה לאור.',
    couldNotUpdateStoryDocument: 'לא ניתן לעדכן מסמך סיפור.',
    couldNotUploadRecording: 'לא ניתן להעלות הקלטה.',
    couldNotUploadStoryDocument: 'לא ניתן להעלות מסמך סיפור.',
    createFirstStoryArtifactFromEditor: 'יצירת חומר הסיפור הראשון מהעורך.',
    createStoryArtifact: 'יצירת חומר סיפור',
    createdFromWritingStudio: 'נוצר מסטודיו הכתיבה',
    currentDraft: 'טיוטה נוכחית',
    currentStatus: 'סטטוס נוכחי',
    currentTarget: 'יעד נוכחי',
    decision: 'החלטה',
    decisionNote: 'הערת החלטה',
    description: 'תיאור',
    designReady: 'מוכן לעיצוב',
    diffAcceptReject: 'השוואה / קבלה / דחייה',
    dismiss: 'דחייה מהתצוגה',
    dismissed: 'נדחה מהתצוגה',
    editStoryArtifact: 'עריכת חומר סיפור',
    editStoryChapter: 'עריכת פרק סיפור',
    editStoryOutline: 'עריכת מתווה סיפור',
    editableStoryText: 'טקסט סיפור לעריכה',
    empty: 'ריק',
    end: 'סוף',
    enterStoryTextBeforeTranslating: 'יש להזין טקסט סיפור לפני תרגום.',
    exportFailedPreflightReviewReport: 'היצוא נכשל בבדיקת הקדם. יש לבדוק את הדוח.',
    externalUrl: 'כתובת חיצונית',
    file: 'קובץ',
    format: 'פורמט',
    aiTranscriptRefinement: 'שיפור תמלול באמצעות בינה מלאכותית',
    generateChapter: 'יצירת פרק',
    generateTranscript: 'יצירת תמלול',
    generatedByWriterAgent: 'נוצר על ידי סוכן כתיבה',
    generatedChapterAcceptedAsNewStoryArtifact: 'הפרק שנוצר התקבל כחומר סיפור חדש.',
    generatedDraft: 'טיוטה שנוצרה',
    generationInstructions: 'הנחיות יצירה',
    heightPercent: 'גובה %',
    interviewStudioDescription: 'ממפגש להקלטה, לתמלול ולחומר מקור מאושר.',
    interviewStudioRecordingFor: 'הקלטת סטודיו ראיונות עבור {title}',
    interviewSession: 'מפגש ראיון',
    interviewTitleAndIntervieweeRequired: 'חובה להזין כותרת ראיון ומרואיין.',
    invalidProjectId: 'מזהה פרויקט לא תקין.',
    isbn: 'ISBN',
    join: 'הצטרפות',
    loadProjectToUseInterviewStudio: 'יש לטעון פרויקט כדי להשתמש בסטודיו הראיונות.',
    ltr: 'משמאל לימין',
    manualInterviewStudioEdit: 'עריכה ידנית בסטודיו הראיונות',
    manualStudioSave: 'שמירה ידנית בסטודיו',
    meetingLink: 'קישור מפגש',
    method: 'שיטה',
    min: 'דק׳',
    modifiable: 'ניתן לשינוי',
    newChapter: 'פרק חדש',
    newManualTranscript: 'תמלול ידני חדש',
    no: 'אין',
    noArtifactsYet: 'אין עדיין חומרים.',
    noDescription: 'אין תיאור',
    noFileSelected: 'לא נבחר קובץ',
    noPendingSuggestions: 'אין הצעות ממתינות.',
    noPreview: 'אין תצוגה מקדימה',
    noReviewCommentsYet: 'אין עדיין תגובות סקירה.',
    noReviewsRequestedYet: 'לא התבקשו עדיין סקירות.',
    noSessionsYet: 'אין עדיין מפגשים.',
    noStoryArtifactSelected: 'לא נבחר חומר סיפור',
    noStoryDocumentsReadyYet: 'אין עדיין מסמכי סיפור מוכנים.',
    noStoryOutlinesYet: 'אין עדיין מתווי סיפור.',
    noStudioVersionsYet: 'אין עדיין גרסאות סטודיו.',
    noSummary: 'אין תקציר',
    noTextSourcesAvailableYet: 'אין עדיין מקורות טקסט זמינים.',
    openReviews: 'סקירות פתוחות',
    operationsDescription: 'מעקב אחר תקינות שירותי ההפקה.',
    order: 'סדר',
    outline: 'מתווה',
    outlineJson: 'JSON מתווה',
    outlineTitle: 'כותרת מתווה',
    projectTitleAndLanguageRequired: 'חובה להזין כותרת פרויקט ושפה.',
    projectTitleOwnerAndLanguageRequired: 'חובה להזין כותרת פרויקט, אחראי ושפה.',
    proposedText: 'טקסט מוצע',
    publish: 'פרסום',
    publishingReady: 'מוכן לפרסום',
    rangeOnly: 'טווח בלבד',
    recording: 'הקלטה',
    recordingDescription: 'העלאת אודיו/וידאו אחרי מפגש Zoom או ממקליט חיצוני.',
    refineTranscript: 'שיפור תמלול בבינה מלאכותית',
    refresh: 'רענון',
    refreshServiceHealth: 'רענון תקינות שירותים',
    refreshingWritingStudio: 'מרענן את סטודיו הכתיבה...',
    reject: 'דחייה',
    reopen: 'פתיחה מחדש',
    request: 'בקשה',
    resolve: 'טיפול',
    restoreToEditor: 'שחזור לעורך',
    review: 'סקירה',
    reviewCommentStatusFilter: 'מסנן סטטוס תגובות סקירה',
    reviewCommentsNeedAnchor: 'תגובות סקירה צריכות עוגן.',
    reviewCommentsYet: 'תגובות סקירה עדיין.',
    reviewInstructions: 'הנחיות סקירה',
    reviewTarget: 'יעד סקירה',
    reviewTargetAndCommentRequired: 'חובה להזין יעד סקירה ותגובה.',
    reviewTargetRequired: 'חובה לבחור יעד סקירה.',
    reviewTargetType: 'סוג יעד סקירה',
    reviewTitle: 'כותרת סקירה',
    reviewable: 'ניתן לסקירה',
    rtl: 'מימין לשמאל',
    saveText: 'שמירת טקסט',
    saveZoom: 'שמירת Zoom',
    savedVersion: 'גרסה {version} נשמרה.',
    sections: 'מקטעים',
    select: 'בחירה',
    selectAtLeastOneSourceBeforeGenerating: 'יש לבחור לפחות מקור אחד לפני יצירה.',
    selectBookAndEnterTitle: 'יש לבחור ספר ולהזין כותרת.',
    selectEditableStudioArtifactAndEnterTitle: 'יש לבחור חומר סטודיו לעריכה ולהזין כותרת.',
    selectProjectToOpenWritingStudio: 'יש לבחור פרויקט לפתיחת סטודיו הכתיבה.',
    selectSession: 'בחירת מפגש',
    selectStoryDocument: 'בחירת מסמך סיפור',
    selectedSources: 'מקורות שנבחרו',
    selectedText: 'טקסט נבחר',
    selectedTextOrTargetNote: 'טקסט נבחר או הערת יעד',
    send: 'שליחה',
    sessions: 'מפגשים',
    scheduleInterview: 'תזמון ראיון',
    sources: 'מקורות',
    start: 'התחלה',
    startTranslationDraft: 'התחלת טיוטת תרגום',
    storyArtifactCreated: 'חומר סיפור נוצר.',
    storyArtifacts: 'חומרי סיפור',
    storyChapter: 'פרק סיפור',
    storyChapterTitleAndBodyRequired: 'חובה להזין כותרת וגוף לפרק.',
    storyDocument: 'מסמך סיפור',
    storyDocuments: 'מסמכי סיפור',
    storyDocumentsRequireWordOrPdf: 'מסמכי סיפור דורשים קובץ Word או PDF.',
    storyDocumentUploaded: 'מסמך הסיפור הועלה.',
    storyOutline: 'מתווה סיפור',
    storyOutlineJsonMustBeValid: 'JSON המתווה חייב להיות תקין.',
    storyOutlineTitleRequired: 'חובה להזין כותרת למתווה.',
    storyPipelineItemCreateStudioArtifact: 'פריט זה מגיע מתהליך הסיפור הקיים. יש ליצור חומר סטודיו כדי לערוך ולשמור גרסאות כאן.',
    storyText: 'טקסט סיפור',
    suggestionAppliedSaveToCreateVersion: 'ההצעה הוחלה על טיוטת העורך. יש לשמור כדי ליצור גרסה.',
    suggestions: 'הצעות',
    summary: 'תקציר',
    targetDirection: 'כיוון יעד',
    targetType: 'סוג יעד',
    textArtifact: 'חומר טקסט',
    textContent: 'תוכן טקסט',
    titleRequired: 'חובה להזין כותרת.',
    total: 'סה״כ',
    transcript: 'תמלול',
    transcriptEditorDescription: 'עריכה ידנית תוך כדי ניגון ההקלטה. כל שמירה יוצרת גרסה.',
    transcriptSessionAndSourceArtifactRequired: 'חובה לבחור מפגש וחומר מקור לתמלול.',
    transcriptSessionAndTextRequired: 'חובה לבחור מפגש ולהזין טקסט תמלול.',
    transcriptTextRequired: 'חובה להזין טקסט תמלול.',
    translationDraft: 'טיוטת תרגום',
    translationDraftFromTranscript: 'טיוטת תרגום מתמלול #{id}',
    translationSuggestionsReady: 'הצעות התרגום מוכנות לסקירה.',
    unsavedManuscriptEditsVisible: 'עריכות כתב יד שלא נשמרו מוצגות בתצוגה המקדימה.',
    updated: 'עודכן',
    upload: 'העלאה',
    uploadArtifact: 'העלאת חומר',
    uploadInterviewRecording: 'העלאת הקלטת ראיון',
    uploadRecording: 'העלאת הקלטה',
    uploadStoryDocument: 'העלאת מסמך סיפור',
    uploadTranscript: 'העלאת תמלול',
    uploader: 'מעלה',
    versionHistory: 'היסטוריית גרסאות',
    versions: 'גרסאות',
    widthPercent: 'רוחב %',
    words: 'מילים',
    writerChat: 'צ׳אט כתיבה',
    writerChatCouldNotRespond: 'צ׳אט הכתיבה לא הצליח להשיב.',
    xPercent: 'X %',
    yPercent: 'Y %',
    zoom: 'Zoom',
    zoomLink: 'קישור Zoom',
    zoomMeetingLink: 'קישור מפגש Zoom',
    zoomMeetingLinkMustBeHttps: 'קישור Zoom חייב להיות כתובת HTTPS של zoom.us.',
    zoomMeetingLinkRequired: 'חובה להזין קישור Zoom.',
  },
  ja: {
    appSubtitle: '制作ワークスペース',
    signInSubtitle: '制作ワークスペースにサインインします。',
    email: 'メール',
    password: 'パスワード',
    signIn: 'サインイン',
    signOut: 'サインアウト',
    loadingWorkspace: 'ワークスペースを読み込み中...',
    primaryNavigation: 'メインナビゲーション',
    invalidEmailOrPassword: 'メールまたはパスワードが正しくありません。',
    projects: 'プロジェクト',
    projectPortal: 'プロジェクトポータル',
    adminConsole: '管理コンソール',
    adminProjects: '管理プロジェクト',
    adminProjectsDescription: 'プラットフォーム全体のプロジェクト作成、所有者、アクセス、制作状態を管理します。',
    publishing: '出版',
    users: 'ユーザー',
    user: 'ユーザー',
    usersAndAccess: 'ユーザーとアクセス',
    usersAndAccessDescription: 'プラットフォームユーザーを作成し、プロジェクトに割り当てる前にアクセス状態を確認できます。',
    refreshUsers: 'ユーザーを更新',
    newUser: '新規ユーザー',
    createUser: 'ユーザー作成',
    createdUser: '作成したユーザー',
    couldNotLoadUsers: 'ユーザーを読み込めませんでした。',
    couldNotCreateUser: 'ユーザーを作成できませんでした。',
    couldNotUpdateUserStatus: 'ユーザー状態を更新できませんでした。',
    username: 'ユーザー名',
    displayName: '表示名',
    phoneNumber: '電話番号',
    temporaryPassword: '仮パスワード',
    temporaryPasswordHelper: '8文字以上にするか、空欄にしてください。',
    temporaryPasswordMinLength: '仮パスワードは8文字以上で入力してください。',
    emailRequired: 'メールは必須です。',
    enterEmailAddress: 'メールアドレスを入力してください。',
    uiLanguage: 'UI言語',
    scope: '範囲',
    admin: '管理者',
    projectScoped: 'プロジェクト限定',
    deactivateUser: 'ユーザーを無効化',
    activateUser: 'ユーザーを有効化',
    noUsersYet: 'ユーザーはまだありません。',
    creating: '作成中...',
    audit: '監査',
    auditTrail: '監査履歴',
    auditTrailDescription: 'プロジェクト作成、メンバー変更、ステータス変更、承認履歴を確認します。',
    couldNotLoadProjects: 'プロジェクトを読み込めませんでした。',
    couldNotLoadAuditEvents: '監査イベントを読み込めませんでした。',
    openProject: 'プロジェクトを開く',
    time: '時刻',
    action: 'アクション',
    target: '対象',
    actor: '実行者',
    noAuditEventsForProject: 'このプロジェクトの監査イベントはありません。',
    noProjectAuditEvents: 'プロジェクト監査イベントはありません。',
    operations: '運用',
    projectWorkspace: 'プロジェクト管理',
    projectWorkspaceDescription: 'プロジェクト、人、言語、承認、監査履歴を含む制作パイプラインを管理します。',
    refreshProjects: 'プロジェクトを更新',
    newProject: '新規プロジェクト',
    createUserFirst: '最初のプロジェクトを開始する前にユーザーを作成してください。',
    activeProjects: '進行中のプロジェクト',
    inReview: 'レビュー中',
    pendingApproval: '承認待ち',
    project: 'プロジェクト',
    status: 'ステータス',
    storyType: 'ストーリー種別',
    language: '言語',
    owner: '担当者',
    role: '役割',
    actions: '操作',
    assign: '割り当て',
    assignTeam: 'チームを割り当て',
    unassign: '解除',
    noProductionNotes: '制作メモはまだありません',
    unassigned: '未割り当て',
    open: '開く',
    noProjectsYet: 'プロジェクトはまだありません。',
    createProject: 'プロジェクト作成',
    projectName: 'プロジェクト名',
    goalsAndInstructions: '目標と指示',
    projectManager: 'プロジェクト管理者',
    package: 'パッケージ',
    primaryLanguage: '主要言語',
    cancel: 'キャンセル',
    create: '作成',
    saveProject: 'プロジェクトを保存',
    publishingDashboard: '出版ダッシュボード',
    publishingDashboardDescription: '進行中のブックプロジェクト全体でKDPパッケージと出版申請を追跡します。',
    refreshPublishingRequests: '出版申請を更新',
    totalRequests: '申請合計',
    ready: '準備完了',
    submitted: '提出済み',
    published: '公開済み',
    allStatuses: 'すべてのステータス',
    provider: 'プロバイダー',
    allProviders: 'すべてのプロバイダー',
    book: '書籍',
    submittedDate: '提出日',
    reference: '参照',
    none: 'なし',
    notRecorded: '未記録',
    noMarketplace: 'マーケットプレイスなし',
    loadingPublishingRequests: '出版申請を読み込み中...',
    noPublishingRequestsMatch: 'この条件に一致する出版申請はありません。',
    packageReady: 'パッケージ',
    preflight: 'プリフライト',
    settings: '設定',
    assets: '素材',
    interviewsAndTranscripts: 'インタビューと文字起こし',
    interviewStudio: 'インタビュースタジオ',
    storyStudio: 'ストーリースタジオ',
    storyOutlines: 'ストーリーアウトライン',
    storyFiles: 'ストーリーファイル',
    chapters: '章',
    targetLanguage: '対象言語',
    textDirection: '文字方向',
    translateDraft: '下書きを翻訳',
    translation: '翻訳',
    overview: '概要',
    team: 'チーム',
    loadingProject: 'プロジェクトを読み込み中...',
    refreshProject: 'プロジェクトを更新',
    projectSnapshot: 'プロジェクト概要',
    interviewSessions: 'インタビュー',
    transcripts: '文字起こし',
    interviewTitle: 'インタビュー名',
    type: '種類',
    audio: '音声',
    video: '動画',
    story_document: 'ストーリー文書',
    document: '文書',
    other: 'その他',
    interviewer: '聞き手',
    interviewee: '話し手',
    scheduled: '予定',
    notScheduled: '未予定',
    minutes: '分',
    notes: 'メモ',
    add: '追加',
    update: '更新',
    generate: '生成',
    session: 'セッション',
    participants: '参加者',
    duration: '時間',
    pending: '保留中',
    saved: '保存済み',
    noInterviewSessionsYet: 'インタビューセッションはまだありません。',
    sourceArtifact: '元素材',
    confidence: '信頼度',
    transcriptText: '文字起こしテキスト',
    source: 'ソース',
    created: '作成日',
    manualTranscript: '手動文字起こし',
    editTranscript: '文字起こしを編集',
    approveTranscript: '文字起こしを承認',
    noTranscriptsYet: '文字起こしはまだありません。',
    artifacts: '素材',
    design: 'デザイン',
    interviews: 'インタビュー',
    publishingStage: '出版',
    reviews: 'レビュー',
    story: 'ストーリー',
    workflow: 'ワークフロー',
    notSet: '未設定',
    bookContent: '書籍コンテンツ',
    content: 'コンテンツ',
    image: '画像',
    spacer: 'スペーサー',
    pageBreak: '改ページ',
    subHeading: '小見出し',
    subChapter: 'サブ章',
    subSubChapter: 'サブサブ章',
    quotedText: '引用テキスト',
    bodyText: '本文',
    alignment: '配置',
    moveUp: '上へ移動',
    moveDown: '下へ移動',
    addSection: 'セクションを追加',
    sectionTitle: 'セクションタイトル',
    titleLevel: 'タイトルレベル',
    startsOn: '開始位置',
    indentLevel: 'インデントレベル',
    headingText: '見出しテキスト',
    headingLevel: '見出しレベル',
    paragraphText: '段落テキスト',
    paragraphStyle: 'スタイル',
    addParagraph: '段落を追加',
    addSubChapter: 'サブ章を追加',
    addSubSubChapter: 'サブサブ章を追加',
    deleteHeading: '見出しを削除',
    deleteParagraph: '段落を削除',
    mergeParagraph: '次の段落と結合',
    imageLayout: 'レイアウト',
    layoutInline: 'インライン',
    layoutFloat: 'フロート',
    layoutFullWidth: '全幅',
    layoutFullPage: '全ページ',
    layoutFullBleed: '裁ち落とし',
    sideNext: '次のページ',
    sideLeft: '左ページ',
    sideRight: '右ページ',
    untitledSection: '無題のセクション',
    fixedPages: '固定ページ',
    fixedPage: '固定ページ',
    fixedSpread: '固定見開き',
    noSectionsYet: 'セクションがありません。追加して開始してください。',
    sectionEmpty: 'このセクションは空です。下にブロックを追加してください。',
    dragImagesHint: '画像を本文にドラッグして段落の間に配置します。',
    dragImageToPlace: '本文にドラッグして配置',
    dropImageHere: 'ここに画像をドロップ',
    removeSection: 'セクションを削除',
    spacerHeightCm: '高さ',
    breakToSide: '改ページ先',
    fixedUnitPreserved: '固定レイアウト — キャンバスエディタで編集(近日公開)。保存時に保持されます。',
    blocks: 'ブロック',
    collapse: '折りたたむ',
    expand: '展開',
    showMore: 'もっと見る',
    addPage: 'ページを追加',
    addSpread: '見開きを追加',
    pageRole: 'ページの役割',
    roleContent: 'コンテンツ',
    roleFullBleedImage: '裁ち落とし画像',
    roleTitle: 'タイトル',
    rolePartDivider: 'パート区切り',
    textBlock: 'テキストブロック',
    imageBlock: '画像ブロック',
    noPhoto: '写真なし',
    background: '背景',
    backgroundType: '背景の種類',
    backgroundImage: '背景画像',
    fit: 'フィット',
    fitCover: 'カバー',
    fitContain: '含む',
    focalX: '焦点 X %',
    focalY: '焦点 Y %',
    selectedBlock: '選択したブロック',
    canvasEmpty: '空のページ。画像またはテキストブロックを追加してください。',
    selectBlockHint: 'キャンバス上のブロックを選択して編集します。',
    removeBlock: 'ブロックを削除',
    fontSize: 'フォントサイズ (pt)',
    positionX: '位置 X %',
    positionY: '位置 Y %',
    rotation: '回転 °',
    layer: 'レイヤー (z)',
    imageFilter: 'フィルター',
    filterNone: 'なし',
    filterGrayscale: 'グレースケール',
    filterSepia: 'セピア',
    onPage: 'ページ',
    bothPages: '両ページ',
    frontMatter: '前付け',
    bodyContent: '本文',
    backMatter: '後付け',
    pageBackground: 'ページ背景',
    pageBackgroundHelp:
      '各ページの背景に表示する既定の画像を設定します。全ページに1枚、または左右で異なる画像を使えます。',
    enableDefaultBackground: '既定の背景を有効化',
    backgroundScope: '適用範囲',
    allPages: '全ページ',
    differentSides: '左右で別々',
    placementCover: 'カバー(塗りつぶし・切り抜き)',
    placementContain: '含める(フィット)',
    placementStretch: '引き伸ばし',
    placementTile: 'タイル',
    designWorkspace: 'デザインワークスペース',
    designParameters: 'デザイン設定',
    designTemplates: 'デザインテンプレート',
    designTemplatesDescription: 'プロジェクトのユーザーが書籍に適用できる、再利用可能なページサイズ・フォント・ヘッダー・間隔のプリセットです。',
    newDesignTemplate: '新規テンプレート',
    editDesignTemplate: 'テンプレートを編集',
    createDesignTemplate: 'テンプレートを作成',
    couldNotLoadDesignTemplates: 'デザインテンプレートを読み込めませんでした。',
    couldNotSaveDesignTemplate: 'デザインテンプレートを保存できませんでした。',
    designTemplateCreated: 'デザインテンプレートを作成しました。',
    designTemplateUpdated: 'デザインテンプレートを更新しました。',
    noDesignTemplatesYet: 'デザインテンプレートはまだありません。',
    templateNameRequired: 'テンプレート名を入力してください。',
    bookType: '書籍タイプ',
    basicInfo: '基本情報',
    indentationAndRules: 'インデントとルール',
    saveChanges: '変更を保存',
    archive: 'アーカイブ',
    restore: '復元',
    archived: 'アーカイブ済み',
    edit: '編集',
    duplicate: '複製',
    copyNoun: 'コピー',
    headers: 'ページヘッダー',
    picture: '絵本',
    mixed: '混合',
    applyTemplate: 'テンプレートを適用',
    applyDesignTemplate: 'デザインテンプレートを適用',
    selectTemplate: 'テンプレートを選択',
    noDesignTemplatesAvailable: '利用可能なデザインテンプレートはまだありません。',
    applyingTemplateReplacesSettings: 'テンプレートを適用すると、現在の印刷・フォント・間隔の設定がテンプレートの内容に置き換わります。自動保存はされません — パラメータダイアログから確認して適用するか、書籍を保存して確定してください。',
    templateApplied: 'テンプレートを適用しました。設定を確認して保存してください。',
    bookDesignDrafts: '書籍デザイン',
    activeBookDesignDraft: '有効なデザイン',
    noActiveBookDesignDraft: '有効なデザインはありません',
    selectOrCreateBookDesignDraft: 'デザインを編集する前に、既存の書籍デザインを選択するか新しいデザインを作成してください。',
    createBookDesignDraft: 'デザインを作成',
    bookDesignDraftName: 'デザイン名',
    bookDesignDraftNameRequired: 'デザイン名を入力してください。',
    couldNotCreateBookDesignDraft: '書籍デザインを作成できませんでした。',
    couldNotDeleteBookDesignDraft: '書籍デザインを削除できませんでした。',
    designTemplate: 'デザインテンプレート',
    noTemplate: 'テンプレートなし',
    creatingUserRecordedFromSession: '作成ユーザーは現在のセッションから記録されます。',
    selectBookDesignDraft: 'デザインを選択',
    noBookDesignDraftsYet: '書籍デザインはまだありません。',
    deleteBookDesignDraft: '書籍デザインを削除',
    bookDetails: '書籍詳細',
    coverDesign: '表紙デザイン',
    startDesignDraft: 'デザインを開始',
    noDesignArtifactsYet: 'デザイン素材はまだありません。',
    parameter: '項目',
    value: '値',
    unit: '単位',
    cm: 'cm',
    element: '要素',
    lineHeight: '行間',
    style: 'スタイル',
    paragraph: '段落',
    photos: '写真',
    cover: '表紙',
    printParameters: '印刷設定',
    fonts: 'フォント',
    indentations: 'インデント',
    rules: 'ルール',
    typography: 'タイポグラフィ',
    visuals: 'ビジュアル',
    rendererPreview: 'プレビュー',
    noExactPreviewYet: '正確なプレビューはまだありません',
    exactPreviewRequiresRenderer: 'PDF出力に使われるものと同じレンダラー成果物を見るには、プレビューを更新してください。',
    loadingPreview: 'プレビューを読み込み中...',
    couldNotLoadPdfPreview: 'PDFプレビューを読み込めませんでした。',
    previewMode: 'プレビューモード',
    singlePage: '単ページ',
    twoPageSpread: '見開き',
    zoomIn: '拡大',
    zoomOut: '縮小',
    resetZoomToFit: '枠に合わせる',
    actualSize: '実寸大（クリックで100%）',
    production: '制作',
    runPreflight: 'プリフライト実行',
    sendToPrinting: '印刷へ送信',
    publishingNotes: '出版メモ',
    printProvider: '印刷プロバイダー',
    submissionTracker: '提出トラッカー',
    history: '履歴',
    title: 'タイトル',
    subtitle: 'サブタイトル',
    authorName: '著者名',
    direction: '方向',
    manuscriptText: '原稿テキスト',
    searchManuscript: '原稿を検索',
    draft: '下書き',
    intake: '受付',
    interview_planned: 'インタビュー予定',
    interview_in_progress: 'インタビュー中',
    transcription_in_progress: '文字起こし中',
    story_outline_in_progress: '構成作成中',
    story_writing_in_progress: '本文作成中',
    review_in_progress: 'レビュー中',
    design_in_progress: 'デザイン中',
    approval_pending: '承認待ち',
    publishing_in_progress: '出版処理中',
    completed: '完了',
    on_hold: '保留',
    cancelled: 'キャンセル',
    package_ready: 'パッケージ準備完了',
    changes_requested: '変更依頼',
    approved: '承認済み',
    rejected: '却下',
    active: '有効',
    resolved: '解決済み',
    openStatus: '未対応',
    deactivated: '無効',
    publishing_ready: '出版準備完了',
    design_ready: 'デザイン準備完了',
    passed: '合格',
    failed: '失敗',
    warning: '警告',
    life_book: '人生・レガシーブック',
    family_book: '家族の本',
    community_book: 'コミュニティブック',
    cookbook: '料理本',
    picture_book: '写真絵本',
    video_clip: '動画クリップ',
    presentation: 'プレゼンテーション',
    self_service: 'セルフサービス',
    guided: 'ガイド付き',
    full_service: 'フルサービス',
    project_manager: 'プロジェクト管理者',
    supervisor: 'スーパーバイザー',
    service_provider: 'サービス提供者',
    writer: 'ライター',
    designer: 'デザイナー',
    reviewer: 'レビュアー',
    amazon_kdp: 'Amazon KDP',
    other_provider: 'その他のプロバイダー',
    print_pdf: '印刷PDF',
    proof_pdf: '校正PDF',
    renderer_preview: 'プレビュー',
    draft_design: 'デザイン下書き',
    approved_for_export: '承認済みデザイン',
    layout_review: 'レイアウトレビュー',
    story_chapter: '章',
    regular_text: '本文',
    chapter_title: '章タイトル',
    sub_chapter: '小見出し',
    sub_sub_chapter: '小小見出し',
    quoted_text: '引用',
    after_paragraph: '段落後',
    top_of_next_page: 'ページ上部',
    full_page: '全ページ',
    absolute: 'ページ上に配置',
    next: '次',
    left: '左',
    right: '右',
    center: '中央',
    outer: '外側',
    inner: '内側',
    justify: '両端揃え',
    front: '表',
    back: '裏',
    photo: '写真',
    text: 'テキスト',
    grayscale: 'グレースケール',
    sepia: 'セピア',
    page: 'ページ',
    check: 'チェック',
    createBook: '書籍を作成',
    matches: '一致',
    placed: '配置済み',
    items: '項目',
    anchor: 'アンカー',
    placement: '配置',
    pageSide: 'ページ側',
    align: '整列',
    rotate: '回転',
    filter: 'フィルター',
    cropTopPercent: '上トリミング %',
    cropRightPercent: '右トリミング %',
    cropBottomPercent: '下トリミング %',
    cropLeftPercent: '左トリミング %',
    caption: 'キャプション',
    addPhoto: '写真を追加',
    layout: 'レイアウト',
    movePhotoToCurrentPosition: '写真を現在位置へ移動',
    removePhoto: '写真を削除',
    noPhotosPlacedYet: '配置済み写真はまだありません。',
    side: '面',
    item: '項目',
    position: '位置',
    coverText: '表紙テキスト',
    coverBackgroundFront: '表紙背景色',
    coverBackgroundBack: '裏表紙背景色',
    coverDefaultFont: 'テーマの既定',
    addCover: '表紙を追加',
    moveCoverItemToCurrentPosition: '表紙項目を現在位置へ移動',
    removeCoverItem: '表紙項目を削除',
    noCoverItemsYet: '表紙項目はまだありません。',
    widthCm: '幅',
    heightCm: '高さ',
    textAlign: 'テキスト整列',
    startPage: '開始ページ',
    innerMarginCm: '内側余白',
    outerMarginCm: '外側余白',
    topMarginCm: '上余白',
    bottomMarginCm: '下余白',
    rightPageHeader: '右ページヘッダー',
    leftPageHeader: '左ページヘッダー',
    headerTokensHint: 'トークン: {page_number}, {book_title}, {author_name}, {chapter_title}',
    rightHeaderTopCm: '右ヘッダー上',
    leftHeaderTopCm: '左ヘッダー上',
    rightHeaderAlign: '右ヘッダー整列',
    leftHeaderAlign: '左ヘッダー整列',
    font: 'フォント',
    size: 'サイズ',
    color: '色',
    bold: '太字',
    italic: '斜体',
    leftToRight: '左から右',
    rightToLeft: '右から左',
    spacingAndRules: '余白とルール',
    paragraphIndentCm: '段落インデント',
    subParagraphCm: '小段落',
    subSubParagraphCm: '小小段落',
    chapterStart: '章の開始',
    contentIndex: '目次',
    generateContentIndex: '目次を生成',
    contentIndexTitle: '目次タイトル',
    includeSubChapters: '小章を含める',
    includeSubSubChapters: '小小章を含める',
    contentIndexDotLeaders: 'ドットリーダー',
    leftPage: '左ページ',
    rightPage: '右ページ',
    previousPage: '前ページ',
    nextPage: '次ページ',
    chapterAboveCm: '章前',
    chapterBelowCm: '章後',
    subChapterAboveCm: '小章前',
    subChapterBelowCm: '小章後',
    subSubAboveCm: '小小章前',
    subSubBelowCm: '小小章後',
    textAboveCm: '本文前',
    textBelowCm: '本文後',
    paragraphEndMarker: '段落終了マーカー',
    marker: 'マーカー',
    markerOffsetCm: 'マーカー位置',
    oneWord: '一語',
    oneLine: '一行',
    pages: 'ページ',
    refreshPreview: 'プレビュー更新',
    renderedBookProof: 'レンダリング済み校正',
    untitledBook: '無題の本',
    noManuscriptTextYet: '原稿テキストはまだありません。',
    saveBook: '書籍を保存',
    applyAndRefreshPreview: '適用してプレビューを更新',
    versionNote: 'バージョンメモ',
    saveVersion: 'バージョン保存',
    approveBook: '書籍を承認',
    exportType: 'エクスポート種別',
    requestExport: 'エクスポート依頼',
    export: 'エクスポート',
    failedCount: '失敗',
    warningsCount: '警告',
    message: 'メッセージ',
    noMessageRecorded: 'メッセージは記録されていません。',
    noPreflightReport: 'このエクスポートのプリフライトレポートはありません。',
    noExportReadyForPreflight: 'プリフライト可能なエクスポートはありません。',
    marketplace: 'マーケットプレイス',
    reviewerNotes: 'レビュアーメモ',
    interior: '本文',
    save: '保存',
    accept: '承認',
    acceptAsNewChapter: '新しい章として承認',
    acceptedGeneratedChapter: '生成章を承認しました',
    ai: 'AI',
    aiChapterFromSources: '素材からAI章を生成',
    ai_draft: 'AI下書き',
    appendToEmptyDraft: '空の下書きに追加',
    approve: '承認',
    approveStoryChapter: '章を承認',
    approveStoryOutline: 'アウトラインを承認',
    approvedOutline: '承認済みアウトライン',
    approvedStoryOutlineRequired: '承認済みアウトラインが必要です。',
    approvedTranscript: '承認済み文字起こし',
    approvedTranscriptRequired: '承認済み文字起こしが必要です。',
    artifact: '素材',
    howAdded: '追加方法',
    creatingUser: '作成ユーザー',
    current: '現在',
    nameDescription: '名前 / 説明',
    commonParameters: '共通パラメーター',
    fileDetails: 'ファイル詳細',
    openArtifactFile: '素材ファイルを開く',
    openExternalSource: '外部ソースを開く',
    noPreviewAvailable: 'プレビューはありません。',
    uploaded: 'アップロード',
    linked: 'リンク',
    close: '閉じる',
    artifactTitleRequired: '素材タイトルは必須です。',
    asin: 'ASIN',
    askForEditOrInsertion: '編集または挿入を依頼',
    assistant: 'アシスタント',
    author: '著者',
    bookTitleRequired: '書籍タイトルは必須です。',
    changeNote: '変更メモ',
    changes: '変更',
    chapter: '章',
    book_chapter: '章',
    book_outline: 'アウトライン',
    preface: '序文',
    acknowledgement: '謝辞',
    introduction: 'はじめに',
    afterword: 'あとがき',
    custom: 'カスタム',
    chapterBody: '章本文',
    chapterTitle: '章タイトル',
    chat: 'チャット',
    chooseRecording: '録音を選択',
    chooseRecordingBeforeGeneratingTranscript: '文字起こし生成前に録音を選択してください。',
    chooseTranscriptFile: '文字起こしファイルを選択',
    chooseSessionAndRecordingFileFirst: '先にセッションと録音ファイルを選択してください。',
    comment: 'コメント',
    comments: 'コメント',
    couldNotAcceptGeneratedChapter: '生成章を承認できませんでした。',
    couldNotAddReviewComment: 'レビューコメントを追加できませんでした。',
    couldNotApproveBookResolveComments: '書籍を承認できませんでした。先に未対応コメントを解決してください。',
    couldNotApproveStoryChapter: '章を承認できませんでした。',
    couldNotApproveStoryOutline: 'アウトラインを承認できませんでした。',
    couldNotApproveTranscript: '文字起こしを承認できませんでした。',
    couldNotAssignProjectMember: 'プロジェクトメンバーを割り当てられませんでした。',
    couldNotCheckServiceStatus: 'サービス状態を確認できませんでした。',
    couldNotCreateBook: '書籍を作成できませんでした。',
    couldNotCreateInterviewSession: 'インタビューセッションを作成できませんでした。',
    couldNotCreateProject: 'プロジェクトを作成できませんでした。',
    couldNotCreateStoryArtifact: 'ストーリー素材を作成できませんでした。',
    couldNotGenerateChapterFromSelectedSources: '選択した素材から章を生成できませんでした。',
    couldNotGenerateStoryChapters: '章を生成できませんでした。',
    couldNotGenerateStoryOutline: 'アウトラインを生成できませんでした。',
    couldNotGenerateTranscript: '文字起こしを生成できませんでした。',
    couldNotLoadBookWorkflow: '書籍ワークフローを読み込めませんでした。',
    couldNotLoadInterviewStudio: 'インタビュースタジオを読み込めませんでした。',
    couldNotLoadProjectWorkspace: 'プロジェクトワークスペースを読み込めませんでした。',
    couldNotLoadProjectWorkspaceData: 'プロジェクトワークスペースデータを読み込めませんでした。',
    couldNotLoadPublishingRequests: '出版申請を読み込めませんでした。',
    couldNotLoadWritingStudio: 'ライティングスタジオを読み込めませんでした。',
    couldNotPrepareTranslationSuggestions: '翻訳提案を準備できませんでした。',
    couldNotReadTranscriptFile: '文字起こしファイルを読み込めませんでした。',
    couldNotRecordReviewDecision: 'レビュー判断を記録できませんでした。',
    couldNotRefineTranscript: '文字起こしを改善できませんでした。',
    couldNotRefreshRendererPreview: 'プレビューを更新できませんでした。',
    couldNotRequestExportApproveBook: 'エクスポートを依頼できませんでした。先に書籍を承認してください。',
    couldNotRequestReview: 'レビューを依頼できませんでした。',
    couldNotRunExportPreflight: 'エクスポートのプリフライトを実行できませんでした。',
    couldNotSaveArtifact: '素材を保存できませんでした。',
    couldNotSaveBookNeedsDraft: '書籍を保存できませんでした。原稿下書きが必要です。',
    bookLockedForEditing: 'この書籍デザインは現在のステータスでは編集できません。変更は保存されず、プレビューも更新されませんでした。',
    couldNotSaveBookVersion: '書籍バージョンを保存できませんでした。',
    couldNotSaveProject: 'プロジェクトを保存できませんでした。',
    couldNotSaveStoryArtifactVersion: 'ストーリー素材バージョンを保存できませんでした。',
    couldNotSaveStoryChapter: '章を保存できませんでした。',
    couldNotSaveStoryOutline: 'アウトラインを保存できませんでした。',
    couldNotSaveTranscript: '文字起こしを保存できませんでした。',
    couldNotSaveZoomMeetingLink: 'Zoomリンクを保存できませんでした。',
    couldNotSendExportToPrinting: 'エクスポートを印刷へ送信できませんでした。',
    couldNotSendStoryDocumentToDesign: 'ストーリー文書をデザインへ送信できませんでした。',
    couldNotSendStoryDocumentToPublishing: 'ストーリー文書を出版へ送信できませんでした。',
    couldNotStartReview: 'レビューを開始できませんでした。',
    couldNotUnassignProjectMember: 'プロジェクトメンバーを解除できませんでした。',
    couldNotUpdatePublishingSubmission: '出版提出を更新できませんでした。',
    couldNotUpdateStoryDocument: 'ストーリー文書を更新できませんでした。',
    couldNotUploadRecording: '録音をアップロードできませんでした。',
    couldNotUploadStoryDocument: 'ストーリー文書をアップロードできませんでした。',
    createFirstStoryArtifactFromEditor: 'エディターから最初のストーリー素材を作成します。',
    createStoryArtifact: 'ストーリー素材を作成',
    createdFromWritingStudio: 'ライティングスタジオで作成',
    currentDraft: '現在の下書き',
    currentStatus: '現在の状態',
    currentTarget: '現在の対象',
    decision: '判断',
    decisionNote: '判断メモ',
    description: '説明',
    designReady: 'デザイン準備完了',
    diffAcceptReject: '差分 / 承認 / 却下',
    dismiss: '却下',
    dismissed: '却下済み',
    editStoryArtifact: 'ストーリー素材を編集',
    editStoryChapter: '章を編集',
    editStoryOutline: 'アウトラインを編集',
    editableStoryText: '編集可能なストーリーテキスト',
    empty: '空',
    end: '終了',
    enterStoryTextBeforeTranslating: '翻訳前にストーリーテキストを入力してください。',
    exportFailedPreflightReviewReport: 'エクスポートはプリフライトに失敗しました。レポートを確認してください。',
    externalUrl: '外部URL',
    file: 'ファイル',
    format: '形式',
    aiTranscriptRefinement: 'AI文字起こし改善',
    generateChapter: '章を生成',
    generateTranscript: '文字起こしを生成',
    generatedByWriterAgent: 'ライターエージェントが生成',
    generatedChapterAcceptedAsNewStoryArtifact: '生成章を新しいストーリー素材として承認しました。',
    generatedDraft: '生成下書き',
    generationInstructions: '生成指示',
    heightPercent: '高さ %',
    interviewStudioDescription: 'セッションから録音、文字起こし、承認済み素材まで進めます。',
    interviewStudioRecordingFor: '{title} のインタビュースタジオ録音',
    interviewSession: 'インタビューセッション',
    interviewTitleAndIntervieweeRequired: 'インタビュータイトルと話し手は必須です。',
    invalidProjectId: 'プロジェクトIDが無効です。',
    isbn: 'ISBN',
    join: '参加',
    loadProjectToUseInterviewStudio: 'インタビュースタジオを使うにはプロジェクトを読み込んでください。',
    ltr: 'LTR',
    manualInterviewStudioEdit: 'インタビュースタジオ手動編集',
    manualStudioSave: 'スタジオ手動保存',
    meetingLink: 'ミーティングリンク',
    method: '方法',
    min: '分',
    modifiable: '変更可能',
    newChapter: '新しい章',
    newManualTranscript: '新規手動文字起こし',
    no: 'なし',
    noArtifactsYet: '素材はまだありません。',
    noDescription: '説明なし',
    noFileSelected: 'ファイル未選択',
    noPendingSuggestions: '保留中の提案はありません。',
    noPreview: 'プレビューなし',
    noReviewCommentsYet: 'レビューコメントはまだありません。',
    noReviewsRequestedYet: 'レビュー依頼はまだありません。',
    noSessionsYet: 'セッションはまだありません。',
    noStoryArtifactSelected: 'ストーリー素材が選択されていません',
    noStoryDocumentsReadyYet: '準備済みストーリー文書はまだありません。',
    noStoryOutlinesYet: 'アウトラインはまだありません。',
    noStudioVersionsYet: 'スタジオバージョンはまだありません。',
    noSummary: '概要なし',
    noTextSourcesAvailableYet: '利用できるテキスト素材はまだありません。',
    openReviews: '未対応レビュー',
    operationsDescription: '制作サービスの稼働状態を監視します。',
    order: '順序',
    outline: 'アウトライン',
    outlineJson: 'アウトラインJSON',
    outlineTitle: 'アウトラインタイトル',
    projectTitleAndLanguageRequired: 'プロジェクトタイトルと言語は必須です。',
    projectTitleOwnerAndLanguageRequired: 'プロジェクトタイトル、担当者、言語は必須です。',
    proposedText: '提案テキスト',
    publish: '公開',
    publishingReady: '出版準備完了',
    rangeOnly: '範囲のみ',
    recording: '録音',
    recordingDescription: 'Zoomセッション後、または外部レコーダーから音声/動画をアップロードします。',
    refineTranscript: 'AIで文字起こしを改善',
    refresh: '更新',
    refreshServiceHealth: 'サービス状態を更新',
    refreshingWritingStudio: 'ライティングスタジオを更新中...',
    reject: '却下',
    reopen: '再オープン',
    request: '依頼',
    resolve: '解決',
    restoreToEditor: 'エディターへ復元',
    review: 'レビュー',
    reviewCommentStatusFilter: 'レビューコメント状態フィルター',
    reviewCommentsNeedAnchor: 'レビューコメントにはアンカーが必要です。',
    reviewCommentsYet: 'レビューコメントはまだありません。',
    reviewInstructions: 'レビュー指示',
    reviewTarget: 'レビュー対象',
    reviewTargetAndCommentRequired: 'レビュー対象とコメントは必須です。',
    reviewTargetRequired: 'レビュー対象は必須です。',
    reviewTargetType: 'レビュー対象種別',
    reviewTitle: 'レビュータイトル',
    reviewable: 'レビュー可能',
    rtl: 'RTL',
    saveText: 'テキスト保存',
    saveZoom: 'Zoom保存',
    savedVersion: 'バージョン {version} を保存しました。',
    sections: 'セクション',
    select: '選択',
    selectAtLeastOneSourceBeforeGenerating: '生成前に少なくとも1つの素材を選択してください。',
    selectBookAndEnterTitle: '書籍を選択してタイトルを入力してください。',
    selectEditableStudioArtifactAndEnterTitle: '編集可能なスタジオ素材を選択し、タイトルを入力してください。',
    selectProjectToOpenWritingStudio: 'ライティングスタジオを開くプロジェクトを選択してください。',
    selectSession: 'セッションを選択',
    selectStoryDocument: 'ストーリー文書を選択',
    selectedSources: '選択素材',
    selectedText: '選択テキスト',
    selectedTextOrTargetNote: '選択テキストまたは対象メモ',
    send: '送信',
    sessions: 'セッション',
    scheduleInterview: 'インタビューを予定',
    sources: '素材',
    start: '開始',
    startTranslationDraft: '翻訳下書きを開始',
    storyArtifactCreated: 'ストーリー素材を作成しました。',
    storyArtifacts: 'ストーリー素材',
    storyChapter: '章',
    storyChapterTitleAndBodyRequired: '章タイトルと本文は必須です。',
    storyDocument: 'ストーリー文書',
    storyDocuments: 'ストーリー文書',
    storyDocumentsRequireWordOrPdf: 'ストーリー文書にはWordまたはPDFファイルが必要です。',
    storyDocumentUploaded: 'ストーリー文書をアップロードしました。',
    storyOutline: 'ストーリーアウトライン',
    storyOutlineJsonMustBeValid: 'アウトラインJSONは有効である必要があります。',
    storyOutlineTitleRequired: 'アウトラインタイトルは必須です。',
    storyPipelineItemCreateStudioArtifact: 'この項目は既存のストーリーパイプラインから来ています。ここで手動編集とバージョン管理を行うにはスタジオ素材を作成してください。',
    storyText: 'ストーリーテキスト',
    suggestionAppliedSaveToCreateVersion: '提案をエディター下書きに適用しました。保存するとバージョンが作成されます。',
    suggestions: '提案',
    summary: '概要',
    targetDirection: '対象方向',
    targetType: '対象種別',
    textArtifact: 'テキスト素材',
    textContent: 'テキスト内容',
    titleRequired: 'タイトルは必須です。',
    total: '合計',
    transcript: '文字起こし',
    transcriptEditorDescription: '録音を再生しながら手動編集します。保存ごとにバージョンが作成されます。',
    transcriptSessionAndSourceArtifactRequired: '文字起こしセッションと素材は必須です。',
    transcriptSessionAndTextRequired: '文字起こしセッションとテキストは必須です。',
    transcriptTextRequired: '文字起こしテキストは必須です。',
    translationDraft: '翻訳下書き',
    translationDraftFromTranscript: '文字起こし #{id} からの翻訳下書き',
    translationSuggestionsReady: '翻訳提案をレビューできます。',
    unsavedManuscriptEditsVisible: '未保存の原稿編集がプレビューに表示されています。',
    updated: '更新日',
    upload: 'アップロード',
    uploadArtifact: '素材をアップロード',
    uploadInterviewRecording: 'インタビュー録音をアップロード',
    uploadRecording: '録音をアップロード',
    uploadStoryDocument: 'ストーリー文書をアップロード',
    uploadTranscript: '文字起こしをアップロード',
    uploader: 'アップロード者',
    versionHistory: 'バージョン履歴',
    versions: 'バージョン',
    widthPercent: '幅 %',
    words: '語',
    writerChat: 'ライターチャット',
    writerChatCouldNotRespond: 'ライターチャットが応答できませんでした。',
    xPercent: 'X %',
    yPercent: 'Y %',
    zoom: 'Zoom',
    zoomLink: 'Zoomリンク',
    zoomMeetingLink: 'Zoomミーティングリンク',
    zoomMeetingLinkMustBeHttps: 'ZoomリンクはHTTPSのzoom.us URLである必要があります。',
    zoomMeetingLinkRequired: 'Zoomリンクは必須です。',
  },
};
